import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout, { PageHeader } from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash, User, Storefront, PencilSimple, Printer, CheckCircle, WarningCircle, Copy, ArrowsClockwise, Link as LinkIcon, CalendarCheck, FileText, Database, Lock, ChatCircleText, ChartLineUp, CreditCard, ListBullets, DotsThreeVertical, ShieldCheck, Key, At, ArrowClockwise } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { useStore } from "../context/StoreContext";
import { initTyro, TYRO_ENVS } from "../lib/tyroService";
import TyroCertHarness from "../components/TyroCertHarness";
import TyroReconciliationCard from "../components/TyroReconciliationCard";
import TemplatesTab from "../components/TemplatesTab";
import MessageTemplatesTab from "../components/MessageTemplatesTab";
import MigrateFromPOSModal from "../components/MigrateFromPOSModal";
import ImportGrossModal from "../components/ImportGrossModal";
import GeneralTab from "../components/GeneralTab";
import DataTab from "../components/DataTab";
import AuditLogTab from "../components/AuditLogTab";
import PermissionsModal from "../components/PermissionsModal";

export default function Settings() {
  const [searchParams] = useSearchParams();
  // Allow deep-links like `/settings?tab=printing` — used by wizard CTAs
  // that guide staff straight to the right tab.
  const initialTab = searchParams.get("tab") || "general";
  const [tab, setTab] = useState(initialTab); // general | stores | users | printing | integrations | templates | data

  return (
    <Layout>
      <PageHeader overline="SETTINGS" title="Store & access" />

      <div className="px-8 pt-6 border-b border-[#E5E7EB] bg-white flex gap-0 overflow-x-auto">
        {[
          { id: "general", label: "General" },
          { id: "stores", label: "Stores" },
          { id: "users", label: "Staff & Users" },
          { id: "printing", label: "Printing" },
          { id: "integrations", label: "Integrations" },
          { id: "templates", label: "Templates" },
          { id: "messages", label: "SMS & Email" },
          { id: "data", label: "Data" },
          { id: "audit", label: "Audit log" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`px-5 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap ${
              tab === t.id ? "border-[#1E5FEB] text-[#121216]" : "border-transparent text-zinc-500 hover:text-[#121216]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "general" && <GeneralTab />}
      {tab === "stores" && <StoresTab />}
      {tab === "users" && <UsersTab />}
      {tab === "printing" && <PrintingTab />}
      {tab === "integrations" && <IntegrationsTab />}
      {tab === "templates" && <TemplatesTab />}
      {tab === "messages" && <MessageTemplatesTab />}
      {tab === "data" && <DataTab />}
      {tab === "audit" && <AuditLogTab />}
    </Layout>
  );
}

export function StoresTab() {
  const { stores, refresh, currentStore, setStore } = useStore();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", address: "", phone: "", google_review_url: "", admin_email: "", admin_phone: "" });

  const open = (s) => {
    if (s) { setEditing(s); setForm({ name: s.name, address: s.address || "", phone: s.phone || "", google_review_url: s.google_review_url || "", admin_email: s.admin_email || "", admin_phone: s.admin_phone || "" }); }
    else { setEditing(null); setForm({ name: "", address: "", phone: "", google_review_url: "", admin_email: "", admin_phone: "" }); }
    setShowModal(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/stores/${editing.id}`, form);
      else {
        const { data } = await api.post("/stores", form);
        // Auto-switch to freshly created store
        setStore(data.id);
      }
      setShowModal(false);
      await refresh();
      toast.success(editing ? "Store updated" : "Store added");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete ${s.name}?`)) return;
    try { await api.delete(`/stores/${s.id}`); await refresh(); toast.success("Store deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-zinc-500 max-w-xl">
          Run multiple storefronts from one login. Customers are shared globally; inventory, tickets, and sales are scoped per store.
        </div>
        <button onClick={() => open(null)} data-testid="add-store-btn" className="h-11 px-4 bg-[#1E5FEB] text-white text-[13px] font-semibold flex items-center gap-2 hover:bg-[#1849C8]">
          <Plus size={14} weight="bold" /> ADD STORE
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {stores.map((s) => (
          <div key={s.id} className={`bg-white border p-5 ${currentStore?.id === s.id ? "border-[#1E5FEB]" : "border-[#E5E7EB]"}`} data-testid={`store-card-${s.id}`}>
            <div className="flex items-start justify-between mb-3">
              <Storefront size={24} weight="duotone" />
              {currentStore?.id === s.id && (
                <span className="text-[10px] bg-[#1E5FEB] text-white px-2 py-1 font-semibold uppercase tracking-wider">Active</span>
              )}
            </div>
            <h3 className="font-heading font-bold text-xl tracking-tight mb-1">{s.name}</h3>
            <div className="text-xs font-mono text-zinc-500 space-y-0.5 mb-4">
              {s.address ? <div>{s.address}</div> : <div>—</div>}
              {s.phone ? <div>{s.phone}</div> : null}
            </div>
            <div className="flex gap-2">
              <button onClick={() => open(s)} data-testid={`store-edit-${s.id}`} className="flex-1 h-9 border border-[#E5E7EB] text-xs font-semibold hover:border-[#121216]">
                <PencilSimple size={12} weight="bold" className="inline mr-1" /> EDIT
              </button>
              {currentStore?.id !== s.id && (
                <button onClick={() => { setStore(s.id); window.location.reload(); }} data-testid={`switch-${s.id}`} className="flex-1 h-9 bg-[#121216] text-white text-xs font-semibold">SWITCH</button>
              )}
              {stores.length > 1 && (
                <button onClick={() => del(s)} className="h-9 px-3 border border-[#E5E7EB] hover:border-[#DC2626] hover:text-[#DC2626]">
                  <Trash size={12} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white w-full max-w-md border border-[#E5E7EB]" data-testid="store-modal">
            <div className="border-b border-[#E5E7EB] p-5">
              <div className="overline mb-1">{editing ? "EDIT" : "ADD"} STORE</div>
              <h2 className="font-heading font-bold text-2xl tracking-tight">{editing ? form.name : "New store"}</h2>
            </div>
            <div className="p-5 space-y-3">
              <Field label="Name"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={fld} data-testid="store-name" /></Field>
              <Field label="Address"><input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={fld} /></Field>
              <Field label="Phone"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={fld + " font-mono"} /></Field>
              <Field label="Google Review URL">
                <input
                  value={form.google_review_url}
                  onChange={(e) => setForm({ ...form, google_review_url: e.target.value })}
                  placeholder="https://g.page/r/..."
                  className={fld + " font-mono text-xs"}
                  data-testid="store-review-url"
                />
                <div className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
                  Used by the "SMS Google Review" action after each sale. Get yours from Google Business → Get more reviews.
                </div>
              </Field>
              <div className="border-t border-[#F3F4F6] pt-3 mt-2">
                <div className="text-[10px] uppercase tracking-[0.16em] text-[#9CA3AF] font-medium mb-2">Z-Report recipient (end-of-shift)</div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Admin email">
                    <input
                      type="email"
                      value={form.admin_email}
                      onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                      placeholder="owner@example.com"
                      className={fld + " text-xs"}
                      data-testid="store-admin-email"
                    />
                  </Field>
                  <Field label="Admin phone">
                    <input
                      value={form.admin_phone}
                      onChange={(e) => setForm({ ...form, admin_phone: e.target.value })}
                      placeholder="+61 4XX XXX XXX"
                      className={fld + " font-mono text-xs"}
                      data-testid="store-admin-phone"
                    />
                  </Field>
                </div>
                <div className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
                  Z-report email + SMS go here when staff close a shift. Leave blank to fall back to the primary admin user.
                </div>
              </div>
            </div>
            <div className="flex border-t border-[#E5E7EB]">
              <button type="button" onClick={() => setShowModal(false)} className="flex-1 h-12 text-sm font-semibold">CANCEL</button>
              <button type="submit" data-testid="store-save" className="flex-1 h-12 bg-[#121216] hover:bg-[#1E5FEB] text-white text-sm font-semibold">SAVE</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export function UsersTab() {
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);   // user being edited, or null
  const [resetResult, setResetResult] = useState(null); // {email, temp_password} from admin reset
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "staff", store_id: "", store_ids: [] });
  const [pinModal, setPinModal] = useState(false);
  const [staffPinModal, setStaffPinModal] = useState(null); // user being pin'd, or null
  const [permsModal, setPermsModal] = useState(null); // user whose permissions we're editing
  const [accountModal, setAccountModal] = useState(null); // "email" | "password" | null
  const [hasPin, setHasPin] = useState(null);
  const { user: me, refresh: refreshUser } = useAuth();

  const load = async () => {
    const [u, s] = await Promise.all([api.get("/users"), api.get("/stores")]);
    setUsers(u.data);
    setStores(s.data);
  };
  const loadPinStatus = () =>
    api.get("/users/me/pin-status").then((r) => setHasPin(!!r.data?.has_pin)).catch(() => setHasPin(false));
  useEffect(() => { load(); loadPinStatus(); }, []);

  const storeName = (id) => stores.find((s) => s.id === id)?.name || "—";

  const create = async (e) => {
    e.preventDefault();
    if (form.role === "staff" && form.store_ids.length === 0) {
      return toast.error("Staff users must be assigned at least one store");
    }
    try {
      // Home store = first checked store when multi-selected.
      const home = form.role === "staff" ? (form.store_ids[0] || null) : (form.store_id || null);
      await api.post("/users", {
        email: form.email,
        password: form.password,
        name: form.name,
        role: form.role,
        store_id: home,
        store_ids: form.role === "staff" ? form.store_ids : [],
      });
      setShowModal(false);
      setForm({ email: "", password: "", name: "", role: "staff", store_id: "", store_ids: [] });
      await load();
      toast.success("User created");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try { await api.delete(`/users/${u.id}`); await load(); toast.success("User deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const resetPassword = async (u) => {
    if (!window.confirm(`Generate a new temporary password for ${u.email}?\nShare it with them — they should change it after login.`)) return;
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`);
      setResetResult(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6">
      {/* My Account card — all logged in users */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-4 flex items-center justify-between gap-4 flex-wrap" data-testid="my-account-card">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-50 text-[#1E5FEB] flex items-center justify-center">
            <User size={18} weight="duotone" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">MY ACCOUNT</div>
            <div className="font-heading font-bold text-[15px] text-slate-900 tracking-tight mt-0.5">
              {me?.name || "—"}
            </div>
            <div className="text-[12px] font-mono text-slate-500 mt-0.5" data-testid="my-email">{me?.email}</div>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setAccountModal("email")}
            data-testid="change-email-btn"
            className="h-10 px-4 rounded-full border border-slate-200 hover:border-slate-400 text-[12px] font-semibold text-slate-700 transition-colors"
          >
            CHANGE EMAIL
          </button>
          <button
            onClick={() => setAccountModal("password")}
            data-testid="change-password-btn"
            className="h-10 px-4 rounded-full bg-slate-900 hover:bg-[#1E5FEB] text-white text-[12px] font-semibold transition-colors"
          >
            CHANGE PASSWORD
          </button>
        </div>
      </div>

      {/* Admin PIN card — only shown to admins */}
      {me?.role === "admin" && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-6 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              hasPin ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"
            }`}>
              <Lock size={18} weight="duotone" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">
                ADMIN PIN · DESTRUCTIVE ACTIONS
              </div>
              <div className="font-heading font-bold text-[15px] text-slate-900 tracking-tight mt-0.5">
                {hasPin === null ? "Checking…" : hasPin ? "PIN is active" : "No PIN set"}
              </div>
              <div className="text-[12px] text-slate-500 mt-0.5 max-w-md">
                Required to delete tickets, void sales, or issue refunds. Only you know your PIN.
              </div>
            </div>
          </div>
          <button
            onClick={() => setPinModal(true)}
            data-testid="open-pin-modal"
            className="h-10 px-4 rounded-full bg-slate-900 hover:bg-[#1E5FEB] text-white text-[12px] font-semibold transition-colors"
          >
            {hasPin ? "CHANGE PIN" : "SET PIN"}
          </button>
        </div>
      )}

      <div className="flex items-center justify-end mb-4">
        <button onClick={() => setShowModal(true)} data-testid="add-user-btn" className="h-11 px-4 bg-[#1E5FEB] text-white text-[13px] font-semibold flex items-center gap-2 hover:bg-[#1849C8]">
          <Plus size={14} weight="bold" /> ADD USER
        </button>
      </div>
      <div className="bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden">
        <div className="hidden md:grid grid-cols-[1.6fr_2fr_0.8fr_1.4fr_0.9fr_auto] gap-x-3 px-5 py-3 bg-[#F8FAFC] border-b border-[#E5E7EB] text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500">
          <div>Name</div>
          <div>Email</div>
          <div>Role</div>
          <div>Home store</div>
          <div>Created</div>
          <div className="text-right pr-1">Actions</div>
        </div>
        <div className="divide-y divide-[#F1F5F9]">
          {users.map((u) => (
            <UserRow
              key={u.id}
              u={u}
              isMe={me?.id === u.id}
              storeName={storeName}
              onEdit={() => setEditing(u)}
              onSetPin={() => setStaffPinModal(u)}
              onPerms={() => setPermsModal(u)}
              onResetPw={() => resetPassword(u)}
              onDelete={() => del(u)}
            />
          ))}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={create} className="bg-white w-full max-w-md border border-[#E5E7EB]" data-testid="user-modal">
            <div className="border-b border-[#E5E7EB] p-5">
              <div className="overline mb-1">ADD</div>
              <h2 className="font-heading font-bold text-2xl tracking-tight">New staff member</h2>
            </div>
            <div className="p-5 space-y-3">
              <Field label="Name"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={fld} data-testid="u-name" /></Field>
              <Field label="Email"><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={fld} data-testid="u-email" /></Field>
              <Field label="Password"><input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={fld} data-testid="u-password" /></Field>
              <Field label="Role">
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value, store_id: e.target.value === "admin" ? "" : form.store_id, store_ids: e.target.value === "admin" ? [] : form.store_ids })} className={fld} data-testid="u-role">
                  <option value="staff">Staff</option>
                  <option value="admin">Admin</option>
                </select>
              </Field>
              {form.role === "admin" ? (
                <Field label="Home store (optional)">
                  <select value={form.store_id} onChange={(e) => setForm({ ...form, store_id: e.target.value })} className={fld} data-testid="u-store">
                    <option value="">All stores</option>
                    {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </Field>
              ) : (
                <Field label={`Stores (${form.store_ids.length} selected)`}>
                  <div className="border border-[#E5E7EB] rounded max-h-40 overflow-y-auto bg-white divide-y divide-[#F3F4F6]" data-testid="u-store-multi">
                    {stores.length === 0 ? (
                      <div className="px-3 py-2 text-[12px] text-zinc-500">No stores configured yet.</div>
                    ) : stores.map((s) => {
                      const checked = form.store_ids.includes(s.id);
                      const isHome = form.store_ids[0] === s.id;
                      return (
                        <label
                          key={s.id}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-[#F8FAFC] cursor-pointer text-[13px]"
                          data-testid={`u-store-checkbox-${s.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              // Add / remove this store from the multi-select. First checked
                              // store becomes the home (login-default). If home is unchecked,
                              // the next remaining store takes over.
                              setForm((f) => {
                                const next = e.target.checked
                                  ? [...f.store_ids, s.id]
                                  : f.store_ids.filter((x) => x !== s.id);
                                return { ...f, store_ids: next };
                              });
                            }}
                            className="accent-[#1E5FEB]"
                          />
                          <span className="flex-1">{s.name}</span>
                          {isHome && (
                            <span className="text-[9px] font-bold uppercase tracking-wider bg-[#EFF6FF] text-[#1E40AF] px-1.5 py-0.5 rounded">
                              Home
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </Field>
              )}
              <p className="text-[11px] text-zinc-500 leading-relaxed">
                {form.role === "staff"
                  ? "Tick every store this staff can log into. The first one selected is their login-default (Home). They can switch freely between the ticked stores from the sidebar — no admin PIN needed."
                  : "Admins can access every store. A home store is optional."}
              </p>
            </div>
            <div className="flex border-t border-[#E5E7EB]">
              <button type="button" onClick={() => setShowModal(false)} className="flex-1 h-12 text-sm font-semibold">CANCEL</button>
              <button type="submit" data-testid="u-save" className="flex-1 h-12 bg-[#121216] hover:bg-[#1E5FEB] text-white text-sm font-semibold">CREATE USER</button>
            </div>
          </form>
        </div>
      )}

      <SetAdminPinModal
        open={pinModal}
        hasPin={hasPin}
        onClose={() => setPinModal(false)}
        onSaved={() => { setPinModal(false); loadPinStatus(); }}
      />
      <SetStaffPinModal
        user={staffPinModal}
        onClose={() => setStaffPinModal(null)}
        onSaved={() => { setStaffPinModal(null); load(); }}
      />
      <PermissionsModal
        user={permsModal}
        onClose={() => setPermsModal(null)}
        onSaved={() => { setPermsModal(null); load(); }}
      />
      <ChangeAccountModal
        mode={accountModal}
        currentEmail={me?.email}
        onClose={() => setAccountModal(null)}
        onEmailChanged={() => { setAccountModal(null); refreshUser?.(); }}
      />
      <EditUserModal
        user={editing}
        stores={stores}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
      <ResetPasswordResultModal
        result={resetResult}
        onClose={() => setResetResult(null)}
      />
    </div>
  );
}

// Admin-initiated user edit — name / role / store(s) / new password (all optional).
function EditUserModal({ user, stores, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  useEffect(() => {
    if (user) setForm({
      name: user.name || "",
      role: user.role,
      store_id: user.store_id || "",
      // Load existing multi-store assignment; fall back to [store_id] for
      // legacy single-store accounts so the checklist reflects reality.
      store_ids: (user.store_ids && user.store_ids.length > 0)
        ? user.store_ids
        : (user.store_id ? [user.store_id] : []),
      password: "",
    });
    else setForm(null);
  }, [user]);
  if (!user || !form) return null;

  const save = async (e) => {
    e.preventDefault();
    if (form.role === "staff" && form.store_ids.length === 0) {
      return toast.error("Staff users must be assigned at least one store");
    }
    // First checked store is the home (login-default) for staff.
    const home = form.role === "staff" ? (form.store_ids[0] || "") : (form.store_id || "");
    const payload = {
      name: form.name,
      role: form.role,
      store_id: home,
      store_ids: form.role === "staff" ? form.store_ids : [],
    };
    if (form.password.trim()) payload.password = form.password.trim();
    try {
      await api.put(`/users/${user.id}`, payload);
      toast.success("User updated");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white w-full max-w-md border border-[#E5E7EB]" data-testid="edit-user-modal">
        <div className="border-b border-[#E5E7EB] p-5">
          <div className="overline mb-1">EDIT USER</div>
          <h2 className="font-heading font-bold text-2xl tracking-tight">{user.name}</h2>
          <div className="text-[12px] font-mono text-zinc-500 mt-1">{user.email}</div>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Name"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={fld} data-testid="edit-u-name" /></Field>
          <Field label="Role">
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={fld} data-testid="edit-u-role">
              <option value="staff">Staff</option>
              <option value="admin">Admin</option>
            </select>
          </Field>
          {form.role === "admin" ? (
            <Field label="Home store (optional)">
              <select value={form.store_id} onChange={(e) => setForm({ ...form, store_id: e.target.value })} className={fld} data-testid="edit-u-store">
                <option value="">All stores</option>
                {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
          ) : (
            <Field label={`Stores (${form.store_ids.length} selected)`}>
              <div className="border border-[#E5E7EB] rounded max-h-40 overflow-y-auto bg-white divide-y divide-[#F3F4F6]" data-testid="edit-u-store-multi">
                {stores.map((s) => {
                  const checked = form.store_ids.includes(s.id);
                  const isHome = form.store_ids[0] === s.id;
                  return (
                    <label
                      key={s.id}
                      className="flex items-center gap-2 px-3 py-2 hover:bg-[#F8FAFC] cursor-pointer text-[13px]"
                      data-testid={`edit-u-store-checkbox-${s.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          setForm((f) => {
                            const next = e.target.checked
                              ? [...f.store_ids, s.id]
                              : f.store_ids.filter((x) => x !== s.id);
                            return { ...f, store_ids: next };
                          });
                        }}
                        className="accent-[#1E5FEB]"
                      />
                      <span className="flex-1">{s.name}</span>
                      {isHome && (
                        <span className="text-[9px] font-bold uppercase tracking-wider bg-[#EFF6FF] text-[#1E40AF] px-1.5 py-0.5 rounded">Home</span>
                      )}
                    </label>
                  );
                })}
              </div>
              <div className="text-[11px] text-zinc-500 mt-1">First checked store is the home (login-default). Staff can switch between all ticked stores without an admin PIN.</div>
            </Field>
          )}
          <Field label="New password (leave blank to keep current)">
            <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={fld} data-testid="edit-u-password" placeholder="Only set if you want to change it" />
          </Field>
        </div>
        <div className="flex border-t border-[#E5E7EB]">
          <button type="button" onClick={onClose} className="flex-1 h-12 text-sm font-semibold">CANCEL</button>
          <button type="submit" data-testid="edit-u-save" className="flex-1 h-12 bg-[#121216] hover:bg-[#1E5FEB] text-white text-sm font-semibold">SAVE CHANGES</button>
        </div>
      </form>
    </div>
  );
}

// Shows the admin-generated temp password once so they can copy + hand it over.
function ResetPasswordResultModal({ result, onClose }) {
  const [copied, setCopied] = useState(false);
  if (!result) return null;
  const copy = () => {
    navigator.clipboard.writeText(result.temp_password);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md border border-[#E5E7EB] rounded-2xl" data-testid="reset-pw-result">
        <div className="border-b border-[#E5E7EB] p-5">
          <div className="overline mb-1">TEMPORARY PASSWORD</div>
          <h2 className="font-heading font-bold text-[20px] tracking-tight">Copy &amp; share securely</h2>
          <div className="text-[12px] text-zinc-500 mt-1">{result.email}</div>
        </div>
        <div className="p-5 space-y-4">
          <div className="rounded-xl border-2 border-[#FDE68A] bg-[#FEF3C7] p-4 text-center">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#B45309] font-semibold mb-1">NEW PASSWORD</div>
            <div className="font-mono font-bold text-[22px] text-[#92400E] tracking-widest select-all" data-testid="reset-pw-value">{result.temp_password}</div>
          </div>
          <p className="text-[12px] text-zinc-600 leading-relaxed">
            This is shown <span className="font-semibold">once</span>. Copy it now and share it with the user over Signal / in person. They should change it via Settings → My Account after their next login.
          </p>
          <button
            onClick={copy}
            data-testid="reset-pw-copy"
            className="w-full h-11 rounded-full bg-[#121216] hover:bg-[#1E5FEB] text-white text-[12px] font-semibold transition-colors"
          >{copied ? "COPIED ✓" : "COPY TO CLIPBOARD"}</button>
        </div>
        <div className="flex border-t border-[#E5E7EB]">
          <button onClick={onClose} className="flex-1 h-12 text-sm font-semibold">CLOSE</button>
        </div>
      </div>
    </div>
  );
}

// Change login email (with OTP verification) OR change password
function ChangeAccountModal({ mode, currentEmail, onClose, onEmailChanged }) {
  // mode: "email" | "password" | null
  const [step, setStep] = useState(1); // for email flow: 1 = request, 2 = verify
  const [pwd, setPwd] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (mode) { setStep(1); setPwd(""); setNewEmail(""); setOtp(""); setNewPwd(""); setConfirmPwd(""); }
  }, [mode]);

  if (!mode) return null;

  const requestEmail = async (e) => {
    e.preventDefault();
    if (!pwd) return toast.error("Enter current password");
    if (!newEmail.includes("@")) return toast.error("Enter a valid email");
    setSaving(true);
    try {
      await api.post("/users/me/email/request-change", { current_password: pwd, new_email: newEmail });
      toast.success(`Code sent to ${newEmail}`);
      setStep(2);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const verifyEmail = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) return toast.error("Enter the 6-digit code");
    setSaving(true);
    try {
      await api.post("/users/me/email/confirm-change", { new_email: newEmail, otp });
      toast.success("Email updated");
      onEmailChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (!pwd) return toast.error("Enter current password");
    if (newPwd.length < 8) return toast.error("New password must be at least 8 characters");
    if (newPwd !== confirmPwd) return toast.error("Passwords don't match");
    setSaving(true);
    try {
      await api.post("/users/me/password", { current_password: pwd, new_password: newPwd });
      toast.success("Password updated. Confirmation email sent.");
      onClose?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const title = mode === "email" ? (step === 1 ? "Change login email" : "Verify new email")
    : "Change login password";

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/55 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-[24px] shadow-2xl border border-slate-200/60 overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="change-account-modal">
        <div className="flex items-start justify-between px-6 pt-6 pb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#1E5FEB] to-[#1849C8] flex items-center justify-center shadow-md shadow-blue-500/25">
              {mode === "email" ? <User size={18} weight="bold" className="text-white" /> : <Lock size={18} weight="bold" className="text-white" />}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">ACCOUNT</div>
              <h3 className="font-heading font-bold text-[18px] text-slate-900 leading-tight">{title}</h3>
              <div className="text-[12px] text-slate-500 mt-0.5">
                {mode === "email" && step === 1 && `Current email: ${currentEmail || "—"}`}
                {mode === "email" && step === 2 && `Check ${newEmail} for the 6-digit code.`}
                {mode === "password" && "You'll get a confirmation email when changed."}
              </div>
            </div>
          </div>
        </div>

        {mode === "email" && step === 1 && (
          <form onSubmit={requestEmail} className="px-6 pb-6 space-y-3">
            <Field label="Current password">
              <input type="password" autoComplete="current-password" value={pwd} onChange={(e) => setPwd(e.target.value)} className={fld2} data-testid="chg-email-password" />
            </Field>
            <Field label="New email">
              <input type="email" autoComplete="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className={fld2} placeholder="new@example.com" data-testid="chg-email-new" />
            </Field>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={onClose} className="flex-1 h-11 rounded-xl border border-slate-200 hover:border-slate-400 text-[13px] font-semibold text-slate-700">CANCEL</button>
              <button type="submit" disabled={saving} className="flex-1 h-11 rounded-xl bg-gradient-to-r from-[#1E5FEB] to-[#1849C8] hover:from-[#1849C8] hover:to-[#123a9e] text-white text-[13px] font-semibold disabled:opacity-60 shadow-md shadow-blue-500/25" data-testid="chg-email-send">
                {saving ? "SENDING…" : "SEND CODE →"}
              </button>
            </div>
          </form>
        )}

        {mode === "email" && step === 2 && (
          <form onSubmit={verifyEmail} className="px-6 pb-6 space-y-3">
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">
                6-digit code
              </div>
              <input
                inputMode="numeric" pattern="[0-9]*" maxLength={6} autoComplete="one-time-code" autoFocus
                value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                className="w-full h-16 px-4 text-center font-mono font-bold text-3xl tracking-[0.4em] rounded-xl border-2 border-slate-200 focus:border-[#1E5FEB] focus:ring-4 focus:ring-blue-100 focus:outline-none"
                placeholder="••••••"
                data-testid="chg-email-otp"
              />
              <button type="button" onClick={() => setStep(1)} className="mt-2 text-[11px] font-semibold text-[#1E5FEB] hover:text-[#1849C8]">
                ← Use a different email
              </button>
            </label>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={onClose} className="flex-1 h-11 rounded-xl border border-slate-200 hover:border-slate-400 text-[13px] font-semibold text-slate-700">CANCEL</button>
              <button type="submit" disabled={saving || otp.length < 6} className="flex-1 h-11 rounded-xl bg-gradient-to-r from-[#1E5FEB] to-[#1849C8] hover:from-[#1849C8] hover:to-[#123a9e] text-white text-[13px] font-semibold disabled:opacity-40 shadow-md shadow-blue-500/25" data-testid="chg-email-verify">
                {saving ? "VERIFYING…" : "CONFIRM"}
              </button>
            </div>
          </form>
        )}

        {mode === "password" && (
          <form onSubmit={changePassword} className="px-6 pb-6 space-y-3">
            <Field label="Current password">
              <input type="password" autoComplete="current-password" value={pwd} onChange={(e) => setPwd(e.target.value)} className={fld2} data-testid="chg-pwd-current" />
            </Field>
            <Field label="New password (min 8 chars)">
              <input type="password" autoComplete="new-password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} className={fld2} data-testid="chg-pwd-new" />
            </Field>
            <Field label="Confirm new password">
              <input type="password" autoComplete="new-password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} className={fld2} data-testid="chg-pwd-confirm" />
            </Field>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={onClose} className="flex-1 h-11 rounded-xl border border-slate-200 hover:border-slate-400 text-[13px] font-semibold text-slate-700">CANCEL</button>
              <button type="submit" disabled={saving} className="flex-1 h-11 rounded-xl bg-gradient-to-r from-[#1E5FEB] to-[#1849C8] hover:from-[#1849C8] hover:to-[#123a9e] text-white text-[13px] font-semibold disabled:opacity-60 shadow-md shadow-blue-500/25" data-testid="chg-pwd-save">
                {saving ? "SAVING…" : "UPDATE PASSWORD"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

const fld2 = "w-full h-11 px-3 rounded-xl border border-slate-200 focus:border-[#1E5FEB] focus:ring-2 focus:ring-blue-100 focus:outline-none text-sm bg-white transition-all";

// Set/Change Admin PIN modal
function SetAdminPinModal({ open, hasPin, onClose, onSaved }) {
  // When changing, prefer current PIN; fall back to login password if user forgot PIN.
  const [usePwd, setUsePwd] = useState(false);
  const [currentPin, setCurrentPin] = useState("");
  const [pwd, setPwd] = useState("");
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setCurrentPin(""); setPwd(""); setPin(""); setConfirmPin("");
      // First-time setup always uses login password
      setUsePwd(!hasPin);
    }
  }, [open, hasPin]);
  if (!open) return null;

  const save = async (e) => {
    e.preventDefault();
    if (!/^\d{4,8}$/.test(pin)) return toast.error("New PIN must be 4–8 digits");
    if (pin !== confirmPin) return toast.error("New PINs don't match");
    const body = { pin };
    if (hasPin && !usePwd) {
      if (!currentPin) return toast.error("Enter your current PIN");
      body.current_pin = currentPin;
    } else {
      if (!pwd) return toast.error("Enter your login password");
      body.current_password = pwd;
    }
    setSaving(true);
    try {
      await api.post("/users/me/pin", body);
      toast.success(hasPin ? "PIN updated" : "PIN set");
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/55 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md rounded-[24px] shadow-2xl border border-slate-200/60 overflow-hidden" data-testid="set-pin-modal">
        <div className="flex items-start justify-between px-6 pt-6 pb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#1E5FEB] to-[#1849C8] flex items-center justify-center shadow-md shadow-blue-500/25">
              <Lock size={18} weight="bold" className="text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">SECURITY</div>
              <h3 className="font-heading font-bold text-[18px] text-slate-900 leading-tight">
                {hasPin ? "Change admin PIN" : "Set admin PIN"}
              </h3>
              <div className="text-[12px] text-slate-500 mt-0.5">
                Required for voids, refunds, and ticket deletion.
              </div>
            </div>
          </div>
        </div>
        <div className="px-6 pb-6 space-y-3">
          {hasPin && !usePwd && (
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">Current PIN</div>
              <input
                type="password" inputMode="numeric" pattern="[0-9]*" maxLength={8} autoComplete="off"
                value={currentPin} onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, ""))}
                className="w-full h-14 px-4 text-center font-mono font-bold text-2xl tracking-[0.4em] rounded-xl border-2 border-slate-200 focus:border-[#1E5FEB] focus:ring-4 focus:ring-blue-100 focus:outline-none"
                placeholder="••••"
                data-testid="pin-setup-current"
              />
              <button type="button" onClick={() => setUsePwd(true)} className="mt-2 text-[11px] font-semibold text-[#1E5FEB] hover:text-[#1849C8]">
                Forgot PIN? Use login password
              </button>
            </label>
          )}
          {(!hasPin || usePwd) && (
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">
                {hasPin ? "Login password" : "Your login password (to confirm it's you)"}
              </div>
              <input
                type="password" autoComplete="current-password"
                value={pwd} onChange={(e) => setPwd(e.target.value)}
                className="w-full h-11 px-3 rounded-xl border border-slate-200 focus:border-[#1E5FEB] focus:ring-2 focus:ring-blue-100 focus:outline-none text-sm"
                data-testid="pin-setup-password"
              />
              {hasPin && (
                <button type="button" onClick={() => setUsePwd(false)} className="mt-2 text-[11px] font-semibold text-[#1E5FEB] hover:text-[#1849C8]">
                  ← Use current PIN instead
                </button>
              )}
            </label>
          )}
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">New PIN (4–8 digits)</div>
            <input
              type="password" inputMode="numeric" pattern="[0-9]*" maxLength={8} autoComplete="new-password"
              value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              className="w-full h-14 px-4 text-center font-mono font-bold text-2xl tracking-[0.4em] rounded-xl border-2 border-slate-200 focus:border-[#1E5FEB] focus:ring-4 focus:ring-blue-100 focus:outline-none"
              placeholder="••••"
              data-testid="pin-setup-new"
            />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">Confirm new PIN</div>
            <input
              type="password" inputMode="numeric" pattern="[0-9]*" maxLength={8} autoComplete="new-password"
              value={confirmPin} onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ""))}
              className="w-full h-14 px-4 text-center font-mono font-bold text-2xl tracking-[0.4em] rounded-xl border-2 border-slate-200 focus:border-[#1E5FEB] focus:ring-4 focus:ring-blue-100 focus:outline-none"
              placeholder="••••"
              data-testid="pin-setup-confirm"
            />
          </label>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 h-11 rounded-xl border border-slate-200 hover:border-slate-400 text-[13px] font-semibold text-slate-700">CANCEL</button>
            <button type="submit" disabled={saving} className="flex-1 h-11 rounded-xl bg-gradient-to-r from-[#1E5FEB] to-[#1849C8] hover:from-[#1849C8] hover:to-[#123a9e] text-white text-[13px] font-semibold disabled:opacity-60 shadow-md shadow-blue-500/25" data-testid="pin-setup-save">
              {saving ? "SAVING…" : hasPin ? "UPDATE PIN" : "SET PIN"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

// Admin-driven 4-digit PIN assignment for any user. Two-step UX:
// (1) Admin enters the target user's new 4-digit PIN (twice for typo guard)
//     plus their OWN admin PIN to authorise. Backend requires X-Admin-Pin.
// (2) "Clear PIN" button to remove an existing PIN (admin-PIN gated).
//
// Why an admin sets the staff PIN (not the staff themselves): the PIN is
// also the credential for /api/auth/pin-login, so the admin handing it
// over in-person matches how the rest of the merchant onboarding flows
// already work (admins create staff with initial passwords, etc.).
function SetStaffPinModal({ user, onClose, onSaved }) {
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [adminPin, setAdminPin] = useState("");
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    if (user) { setPin(""); setConfirmPin(""); setAdminPin(""); }
  }, [user]);

  if (!user) return null;

  const save = async (e) => {
    e.preventDefault();
    if (!/^\d{4}$/.test(pin)) return toast.error("PIN must be exactly 4 digits");
    if (pin !== confirmPin) return toast.error("PINs don't match");
    if (!/^\d{4,8}$/.test(adminPin)) return toast.error("Enter your admin PIN to authorise");
    setSaving(true);
    try {
      await api.post(`/users/${user.id}/staff-pin`, { pin }, {
        headers: { "X-Admin-Pin": adminPin },
      });
      toast.success(`PIN set for ${user.name}`);
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const clear = async () => {
    if (!/^\d{4,8}$/.test(adminPin)) return toast.error("Enter your admin PIN to authorise");
    if (!window.confirm(`Remove ${user.name}'s 4-digit PIN? They will no longer be able to confirm staff actions (edit invoice, delete ticket) until you assign a new one.`)) return;
    setClearing(true);
    try {
      await api.delete(`/users/${user.id}/staff-pin`, { headers: { "X-Admin-Pin": adminPin } });
      toast.success(`PIN removed for ${user.name}`);
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setClearing(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/55 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md rounded-[24px] shadow-2xl border border-slate-200/60 overflow-hidden" data-testid="set-staff-pin-modal">
        <div className="flex items-start justify-between px-6 pt-6 pb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center shadow-md shadow-emerald-500/25">
              <Lock size={18} weight="bold" className="text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">STAFF PIN</div>
              <h3 className="font-heading font-bold text-[18px] text-slate-900 leading-tight">
                {user.has_staff_pin ? "Change" : "Assign"} 4-digit PIN
              </h3>
              <div className="text-[12px] text-slate-500 mt-0.5">
                For {user.name} — required when {user.name} edits an invoice or deletes a repair ticket. Every PIN-confirmed action is recorded against {user.name} in the audit log. (Login still uses email + password.)
              </div>
            </div>
          </div>
        </div>
        <div className="px-6 pb-6 space-y-3">
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">New 4-digit PIN</div>
            <input
              type="password" inputMode="numeric" pattern="[0-9]*" maxLength={4} autoComplete="new-password"
              value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              className="w-full h-14 px-4 text-center font-mono font-bold text-2xl tracking-[0.4em] rounded-xl border-2 border-slate-200 focus:border-emerald-600 focus:ring-4 focus:ring-emerald-100 focus:outline-none"
              placeholder="••••"
              data-testid="staff-pin-new"
            />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">Confirm PIN</div>
            <input
              type="password" inputMode="numeric" pattern="[0-9]*" maxLength={4} autoComplete="new-password"
              value={confirmPin} onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ""))}
              className="w-full h-14 px-4 text-center font-mono font-bold text-2xl tracking-[0.4em] rounded-xl border-2 border-slate-200 focus:border-emerald-600 focus:ring-4 focus:ring-emerald-100 focus:outline-none"
              placeholder="••••"
              data-testid="staff-pin-confirm"
            />
          </label>
          <div className="border-t border-slate-100 pt-3">
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">Your admin PIN (to authorise)</div>
              <input
                type="password" inputMode="numeric" pattern="[0-9]*" maxLength={8} autoComplete="off"
                value={adminPin} onChange={(e) => setAdminPin(e.target.value.replace(/\D/g, ""))}
                className="w-full h-12 px-4 text-center font-mono font-bold text-xl tracking-[0.4em] rounded-xl border-2 border-slate-200 focus:border-[#1E5FEB] focus:ring-2 focus:ring-blue-100 focus:outline-none"
                placeholder="••••"
                data-testid="staff-pin-admin"
              />
            </label>
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 h-11 rounded-xl border border-slate-200 hover:border-slate-400 text-[13px] font-semibold text-slate-700">CANCEL</button>
            <button type="submit" disabled={saving} className="flex-1 h-11 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white text-[13px] font-semibold disabled:opacity-60 shadow-md shadow-emerald-500/25" data-testid="staff-pin-save">
              {saving ? "SAVING…" : user.has_staff_pin ? "UPDATE PIN" : "SET PIN"}
            </button>
          </div>
          {user.has_staff_pin && (
            <button
              type="button"
              onClick={clear}
              disabled={clearing}
              className="w-full text-[11px] font-semibold uppercase tracking-wider text-[#DC2626] hover:underline disabled:opacity-60"
              data-testid="staff-pin-clear"
            >
              {clearing ? "REMOVING…" : "Remove existing PIN"}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

const fld = "w-full h-10 px-3 border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none text-sm rounded-none bg-white";

// ---------- User row ----------
//
// Clean, low-density layout: avatar + name + email + role chip on the
// left; store + created date in the middle; just THREE control elements
// on the right —
//   1. PERMISSIONS button (the primary action — also shows current
//      template label as a tiny chip when not the default Cashier).
//   2. PIN status pill — green dot when set, gray when missing. Whole
//      pill is clickable to open the SetStaffPinModal.
//   3. Overflow kebab menu (Edit · Reset password · Delete user).
//
// This collapses the previous 5-button cluster into a calm, scannable
// row that still keeps every action one click away.
function UserRow({ u, isMe, storeName, onEdit, onSetPin, onPerms, onResetPw, onDelete }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const initials = (u.name || "?")
    .split(/\s+/).filter(Boolean).slice(0, 2)
    .map((p) => p[0].toUpperCase()).join("");
  const isAdminRow = u.role === "admin";
  const tplLabel = u.permission_template && u.permission_template !== "cashier"
    ? u.permission_template.replace("_", " ")
    : "";

  // Close the kebab when clicking outside. Lightweight effect — only
  // installs the listener while the menu is actually open.
  useEffect(() => {
    if (!menuOpen) return;
    const handler = () => setMenuOpen(false);
    window.addEventListener("click", handler);
    return () => window.removeEventListener("click", handler);
  }, [menuOpen]);

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-[1.6fr_2fr_0.8fr_1.4fr_0.9fr_auto] gap-x-3 gap-y-1 px-5 py-3.5 items-center hover:bg-[#FAFBFC] transition-colors"
      data-testid={`user-row-${u.email}`}
    >
      {/* Name + avatar */}
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${isAdminRow ? "bg-slate-900 text-white" : "bg-blue-50 text-[#1E5FEB] border border-blue-100"}`}>
          {initials}
        </div>
        <div className="min-w-0">
          <div className="text-[13.5px] font-semibold text-slate-900 truncate">{u.name}{isMe && <span className="ml-1.5 text-[10px] font-mono text-zinc-400 font-normal">(you)</span>}</div>
          <div className="text-[11px] text-zinc-500 md:hidden font-mono truncate">{u.email}</div>
        </div>
      </div>

      {/* Email (hidden on mobile, shown above) */}
      <div className="hidden md:block font-mono text-[12px] text-slate-600 truncate">{u.email}</div>

      {/* Role */}
      <div>
        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] px-2 py-1 font-semibold rounded ${isAdminRow ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`}>
          {isAdminRow && <ShieldCheck size={10} weight="bold" />} {u.role}
        </span>
      </div>

      {/* Home store — plus a "+N more" pill when a staff has multiple stores. */}
      <div className="text-[12px] text-slate-700 truncate hidden md:block">
        {u.store_id ? (
          <span className="inline-flex items-center gap-1.5">
            <span className="truncate">{storeName(u.store_id)}</span>
            {(u.store_ids?.length || 0) > 1 && (
              <span
                className="text-[9px] font-bold uppercase tracking-wider bg-[#EFF6FF] text-[#1E40AF] border border-[#BFDBFE] px-1.5 py-0.5 rounded"
                title={u.store_ids.map(storeName).join(", ")}
                data-testid={`user-multi-store-pill-${u.id}`}
              >
                +{u.store_ids.length - 1}
              </span>
            )}
          </span>
        ) : (
          <span className="text-zinc-400 italic">All stores</span>
        )}
      </div>

      {/* Created */}
      <div className="font-mono text-[11px] text-zinc-500 hidden md:block">
        {new Date(u.created_at).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "2-digit" })}
      </div>

      {/* Actions — 3 elements only */}
      <div className="flex items-center justify-end gap-2">
        {/* Primary: permissions (staff only). Admins get a non-clickable
            "All access" chip instead because their permissions are not
            editable. */}
        {isAdminRow ? (
          <div className="h-9 px-3 rounded-lg bg-slate-50 text-slate-500 text-[11px] font-semibold uppercase tracking-wider flex items-center gap-1.5">
            <ShieldCheck size={12} weight="bold" /> All access
          </div>
        ) : (
          <button
            onClick={onPerms}
            data-testid={`perms-${u.email}`}
            className="h-9 px-3 rounded-lg bg-[#1E5FEB]/8 hover:bg-[#1E5FEB]/14 text-[#1E5FEB] text-[11px] font-semibold uppercase tracking-wider flex items-center gap-1.5 transition-colors"
            title="Choose what this staff member can do"
          >
            <ShieldCheck size={12} weight="bold" />
            Permissions
            {tplLabel && (
              <span className="ml-0.5 text-[9.5px] font-mono font-normal bg-white/70 text-[#1E5FEB] px-1.5 py-0.5 rounded">
                {tplLabel}
              </span>
            )}
          </button>
        )}

        {/* PIN status pill — clickable to set/change */}
        <button
          onClick={onSetPin}
          data-testid={`set-staff-pin-${u.email}`}
          className={`h-9 px-3 rounded-lg text-[11px] font-semibold uppercase tracking-wider flex items-center gap-1.5 transition-colors ${
            u.has_staff_pin
              ? "bg-emerald-50 hover:bg-emerald-100 text-emerald-700"
              : "bg-slate-50 hover:bg-slate-100 text-slate-500 hover:text-slate-700"
          }`}
          title={u.has_staff_pin ? "PIN configured — click to change" : "No PIN set — click to assign"}
        >
          <Key size={12} weight="bold" />
          {u.has_staff_pin ? "PIN set" : "No PIN"}
        </button>

        {/* Overflow menu for less-frequent actions */}
        <div className="relative" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => setMenuOpen((o) => !o)}
            data-testid={`user-menu-${u.email}`}
            className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-900 transition-colors"
            title="More actions"
          >
            <DotsThreeVertical size={18} weight="bold" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-full mt-1.5 w-56 bg-white border border-slate-200 rounded-xl shadow-xl shadow-slate-900/5 overflow-hidden z-10" data-testid={`user-menu-pop-${u.email}`}>
              <MenuItem icon={PencilSimple} label="Edit details" onClick={() => { setMenuOpen(false); onEdit(); }} testid={`edit-user-${u.email}`} />
              <MenuItem icon={ArrowClockwise} label="Reset password" onClick={() => { setMenuOpen(false); onResetPw(); }} testid={`reset-pw-${u.email}`} />
              {!isMe && (
                <>
                  <div className="h-px bg-slate-100" />
                  <MenuItem icon={Trash} label="Delete user" destructive onClick={() => { setMenuOpen(false); onDelete(); }} testid={`delete-user-${u.email}`} />
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, destructive, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`w-full text-left px-3.5 py-2.5 flex items-center gap-2.5 transition-colors text-[13px] ${
        destructive
          ? "text-[#DC2626] hover:bg-red-50"
          : "text-slate-700 hover:bg-slate-50"
      }`}
    >
      <Icon size={14} weight="bold" />
      <span className="font-medium">{label}</span>
    </button>
  );
}
function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-semibold mb-1">{label}</div>
      {children}
    </label>
  );
}

// ---------- PrintingTab ----------
export function PrintingTab() {
  const { currentStore } = useStore();
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [status, setStatus] = useState(null); // {configured: bool}
  const [printers, setPrinters] = useState([]);
  const [loadingPrinters, setLoadingPrinters] = useState(false);
  const [prefs, setPrefs] = useState({
    receipt_printer_id: null,
    label_printer_id: null,
    auto_print_receipt: false,
    auto_print_label: false,
  });
  const [saving, setSaving] = useState(false);

  // Re-fetch prefs whenever the active store changes (multi-store safe).
  useEffect(() => {
    api.get("/print/status").then((r) => setStatus(r.data));
  }, []);
  useEffect(() => {
    if (!currentStore?.id) return;
    api.get("/users/me/print-prefs").then((r) => setPrefs(r.data));
  }, [currentStore?.id]);

  const loadPrinters = async () => {
    setLoadingPrinters(true);
    try {
      const { data } = await api.get("/print/printers");
      setPrinters(data);
      if (!data.length) toast.info("Connected, but no printers found. Install PrintNode client & connect a printer.");
      else toast.success(`Found ${data.length} printer${data.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingPrinters(false);
    }
  };

  const savePrefs = async (patch = {}, { applyToStore = false } = {}) => {
    const next = { ...prefs, ...patch };
    if ("receipt_printer_id" in patch && patch.receipt_printer_id && !prefs.receipt_printer_id && !prefs.auto_print_receipt) {
      next.auto_print_receipt = true;
    }
    if ("label_printer_id" in patch && patch.label_printer_id && !prefs.label_printer_id && !prefs.auto_print_label) {
      next.auto_print_label = true;
    }
    setPrefs(next);
    setSaving(true);
    try {
      await api.put("/users/me/print-prefs", next);
      // Admins by default push the same prefs to the store so non-admin staff
      // at the counter inherit them automatically. `applyToStore: false` is
      // only used for explicit personal overrides.
      if (applyToStore && isAdmin) {
        try { await api.put(`/stores/${currentStore.id}/print-prefs`, next); } catch { /* silent */ }
      }
      toast.success(applyToStore && isAdmin ? "Saved · applied to all staff" : "Print preferences saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const isConfigured = status?.configured;

  const [testingPrint, setTestingPrint] = useState(false);
  const runTestPrint = async (jobs) => {
    setTestingPrint(true);
    try {
      const { data } = await api.post("/print/test", { jobs });
      const wins = data.results.filter((r) => r.ok);
      const fails = data.results.filter((r) => !r.ok);
      if (wins.length) {
        toast.success(`Sent ${wins.length} test print${wins.length === 1 ? "" : "s"} to PrintNode. Check the printer.`);
      }
      fails.forEach((f) => toast.error(`${f.job.replace("_", " ")}: ${f.error || "failed"}`));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTestingPrint(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl space-y-6">
      {/* Active-store banner — makes it clear these prefs are scoped */}
      <div
        className="flex items-center gap-3 rounded-full border border-[#DBEAFE] bg-[#EFF6FF] px-4 py-2"
        data-testid="printing-store-scope-banner"
      >
        <Printer size={14} weight="bold" className="text-[#1E5FEB]" />
        <div className="text-[11px] tracking-[0.12em] uppercase text-[#1E5FEB] font-semibold">
          Printing for
        </div>
        <div className="text-[13px] font-semibold text-[#121216] truncate">
          {currentStore?.name || "—"}
        </div>
        <div className="ml-auto text-[10px] text-[#4B5563]">
          Settings apply only to this store. Switch stores from the sidebar to configure others.
        </div>
      </div>

      {/* PrintNode connection status */}
      <section className="rounded-2xl border border-[#E5E7EB] bg-white p-6" data-testid="printnode-status-card">
        <div className="flex items-start gap-4">
          <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${isConfigured ? "bg-[#DCFCE7]" : "bg-[#FEF3C7]"}`}>
            {isConfigured ? (
              <CheckCircle size={22} weight="fill" className="text-[#2F8F6E]" />
            ) : (
              <WarningCircle size={22} weight="fill" className="text-[#C98B24]" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium">
              Cloud printing · PrintNode
            </div>
            <div className="mt-1 font-display text-[20px] font-semibold tracking-tight text-[#121216]">
              {isConfigured ? "Connected" : "Not configured"}
            </div>
            <p className="text-[12px] text-[#4B5563] mt-2 leading-relaxed">
              {isConfigured
                ? "Your PrintNode API key is set. Install the PrintNode client on the counter Mac/PC connected to your Epson + Dymo printers, then tap Refresh printers below."
                : <>Add <code className="font-mono text-[11px] bg-[#F8FAFC] px-1 rounded">PRINTNODE_API_KEY</code> to <code className="font-mono text-[11px] bg-[#F8FAFC] px-1 rounded">backend/.env</code> to enable silent cloud printing. Sign up at <a href="https://printnode.com" target="_blank" rel="noreferrer" className="text-[#1E5FEB] hover:underline">printnode.com</a>.</>
              }
            </p>
          </div>
          <button
            onClick={loadPrinters}
            disabled={!isConfigured || loadingPrinters}
            data-testid="refresh-printers"
            className="h-10 px-4 rounded-full bg-[#121216] hover:bg-[#1E5FEB] disabled:bg-[#E5E7EB] disabled:text-[#9CA3AF] text-white text-[12px] font-semibold flex items-center gap-2 transition-colors"
          >
            <Printer size={13} weight="bold" />
            {loadingPrinters ? "Loading…" : "Refresh printers"}
          </button>
        </div>
      </section>

      {/* Printer selection */}
      <section className="rounded-2xl border border-[#E5E7EB] bg-white p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium">
            Your print setup
          </div>
          <div className="flex items-center gap-1.5 text-[11px] text-[#4B5563]" data-testid="autosave-hint">
            {saving ? (
              <>
                <span className="w-2 h-2 rounded-full bg-[#1E5FEB] animate-pulse" />
                Saving…
              </>
            ) : (
              <>
                <CheckCircle size={12} weight="fill" className="text-[#2F8F6E]" />
                Changes save automatically
              </>
            )}
          </div>
        </div>
        <div className="space-y-5">
          <PrinterSelect
            label="Receipt printer"
            hint="80 mm thermal (Epson TM-T20/T88, Star, etc.)"
            printers={printers}
            value={prefs.receipt_printer_id}
            onChange={(v) => savePrefs({ receipt_printer_id: v }, { applyToStore: true })}
            testid="select-receipt-printer"
            disabled={!isConfigured}
          />
          {prefs.receipt_printer_id && (
            <PaperSelect
              label="Receipt paper size"
              hint="Match this to your Epson driver to avoid blank top/bottom strips."
              printerId={prefs.receipt_printer_id}
              value={prefs.receipt_paper}
              onChange={(v) => savePrefs({ receipt_paper: v }, { applyToStore: true })}
              testid="select-receipt-paper"
              fallbackHint='Leave blank for "Receipt" default'
            />
          )}
          <PrinterSelect
            label="Label printer"
            hint="Dymo LabelWriter 1.125 × 3.5"
            printers={printers}
            value={prefs.label_printer_id}
            onChange={(v) => savePrefs({ label_printer_id: v }, { applyToStore: true })}
            testid="select-label-printer"
            disabled={!isConfigured}
          />
          {prefs.label_printer_id && (
            <PaperSelect
              label="Label paper size"
              hint="Pick the exact Dymo label size (e.g. 30334_2-1_4_in_x_1_1_4_in)."
              printerId={prefs.label_printer_id}
              value={prefs.label_paper}
              onChange={(v) => savePrefs({ label_paper: v }, { applyToStore: true })}
              testid="select-label-paper"
              fallbackHint="Leave blank to use the driver's default"
            />
          )}
        </div>
      </section>

      {/* Auto-print toggles */}
      <section className="rounded-2xl border border-[#E5E7EB] bg-white p-6">
        <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium mb-3">
          Automatic printing · {currentStore?.name || "this store"}
        </div>
        <div className="divide-y divide-[#F3F4F6] -mx-2">
          <ToggleRow
            label="Auto-print receipt on Complete Sale"
            hint="Prints the 80 mm thermal receipt silently when a sale is completed."
            checked={!!prefs.auto_print_receipt}
            onChange={(v) => savePrefs({ auto_print_receipt: v }, { applyToStore: true })}
            disabled={!isConfigured || !prefs.receipt_printer_id}
            testid="toggle-auto-print-receipt"
          />
          <ToggleRow
            label="Auto-print label on New Ticket"
            hint="Prints a Dymo workshop label the moment a repair ticket is created."
            checked={!!prefs.auto_print_label}
            onChange={(v) => savePrefs({ auto_print_label: v }, { applyToStore: true })}
            disabled={!isConfigured || !prefs.label_printer_id}
            testid="toggle-auto-print-label"
          />
        </div>
        {(!isConfigured) && (
          <div className="mt-4 text-[11px] text-[#9CA3AF]">
            Auto-print unlocks once cloud printing is connected and a printer is selected above.
          </div>
        )}

        {isAdmin && isConfigured && (
          <div
            className="mt-3 text-[11px] flex items-start gap-2 px-3 py-2 rounded-lg"
            style={{ background: "#EFF6FF", color: "#1E40AF" }}
            data-testid="store-prefs-hint"
          >
            <CheckCircle size={12} weight="fill" className="mt-0.5 shrink-0" />
            <span>
              <strong>Shared with all staff:</strong> these settings apply to every user at {currentStore?.name || "this store"}. Non-admin staff inherit them automatically — no setup needed on their account.
            </span>
          </div>
        )}
      </section>

      {/* Test print — fire sample jobs to verify printer layout/cut behaviour */}
      <section className="rounded-2xl border border-[#E5E7EB] bg-white p-6" data-testid="test-print-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium mb-1">
              Test printing
            </div>
            <div className="text-[14px] font-semibold text-[#121216]">Fire sample prints</div>
            <div className="text-[12px] text-[#6B7280] mt-1 leading-relaxed max-w-lg">
              Send a sample receipt, label, or ticket slip to the configured printers above.
              Use this to verify layout and cut behaviour without ringing up a real sale.
            </div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={() => runTestPrint(["receipt"])}
            disabled={!isConfigured || !prefs.receipt_printer_id || testingPrint}
            data-testid="test-print-receipt-btn"
            className="h-10 px-4 rounded-xl bg-[#121216] text-white text-[12px] font-semibold tracking-wide disabled:bg-[#E5E7EB] disabled:text-[#9CA3AF] disabled:cursor-not-allowed hover:bg-[#1E5FEB] transition-colors"
          >
            Test Receipt
          </button>
          <button
            onClick={() => runTestPrint(["ticket_slip"])}
            disabled={!isConfigured || !prefs.receipt_printer_id || testingPrint}
            data-testid="test-print-ticket-slip-btn"
            className="h-10 px-4 rounded-xl bg-[#121216] text-white text-[12px] font-semibold tracking-wide disabled:bg-[#E5E7EB] disabled:text-[#9CA3AF] disabled:cursor-not-allowed hover:bg-[#1E5FEB] transition-colors"
          >
            Test Ticket Slip
          </button>
          <button
            onClick={() => runTestPrint(["label"])}
            disabled={!isConfigured || !prefs.label_printer_id || testingPrint}
            data-testid="test-print-label-btn"
            className="h-10 px-4 rounded-xl bg-[#121216] text-white text-[12px] font-semibold tracking-wide disabled:bg-[#E5E7EB] disabled:text-[#9CA3AF] disabled:cursor-not-allowed hover:bg-[#1E5FEB] transition-colors"
          >
            Test Label
          </button>
          <button
            onClick={() => runTestPrint(["receipt", "ticket_slip", "label"])}
            disabled={!isConfigured || testingPrint || (!prefs.receipt_printer_id && !prefs.label_printer_id)}
            data-testid="test-print-all-btn"
            className="h-10 px-4 rounded-xl border border-[#121216] text-[#121216] text-[12px] font-semibold tracking-wide disabled:border-[#E5E7EB] disabled:text-[#9CA3AF] disabled:cursor-not-allowed hover:bg-[#F8FAFC] transition-colors ml-auto"
          >
            Print all three
          </button>
        </div>
      </section>
    </div>
  );
}

function PrinterSelect({ label, hint, printers, value, onChange, testid, disabled }) {
  const selected = value ? printers.find((p) => p.id === value) : null;
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <div className="text-[13px] font-semibold text-[#121216]">{label}</div>
          {value && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded-full bg-[#DCFCE7] text-[#2F8F6E]">
              <CheckCircle size={10} weight="fill" />
              SAVED
            </span>
          )}
        </div>
        <div className="text-[11px] text-[#9CA3AF]">{hint}</div>
      </div>
      <select
        value={value || ""}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
        data-testid={testid}
        className="w-full h-11 px-3 border border-[#E5E7EB] rounded-xl bg-white text-[13px] text-[#121216] focus:border-[#1E5FEB] focus:outline-none focus:ring-4 focus:ring-[#DBEAFE] disabled:bg-[#F8FAFC] disabled:text-[#9CA3AF]"
      >
        <option value="">— Not set —</option>
        {printers.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name} {p.computer_name ? `· ${p.computer_name}` : ""} {p.state ? `· ${p.state}` : ""}
          </option>
        ))}
      </select>
      {value && selected && (
        <div className="mt-1.5 text-[11px] text-[#4B5563]" data-testid={`${testid}-current`}>
          Currently routing to <span className="font-semibold text-[#121216]">{selected.name}</span>
          {selected.computer_name && <> on <span className="font-mono">{selected.computer_name}</span></>}
        </div>
      )}
    </label>
  );
}

function ToggleRow({ label, hint, checked, onChange, disabled, testid }) {
  return (
    <div className="flex items-center justify-between gap-4 px-2 py-4">
      <div className="min-w-0">
        <div className="text-[13px] font-semibold text-[#121216]">{label}</div>
        <div className="text-[11px] text-[#4B5563] mt-0.5">{hint}</div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        data-testid={testid}
        className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${
          disabled ? "bg-[#E5E7EB] cursor-not-allowed" : checked ? "bg-[#1E5FEB]" : "bg-[#E5E7EB]"
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${checked ? "translate-x-5" : ""}`}
        />
      </button>
    </div>
  );
}

// ---------- PaperSelect (fetches driver-advertised paper names) ----------
function PaperSelect({ label, hint, printerId, value, onChange, testid, fallbackHint }) {
  const [papers, setPapers] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [loading, setLoading] = useState(false);
  const [custom, setCustom] = useState(false);

  useEffect(() => {
    if (!printerId) return;
    setLoading(true);
    api.get(`/print/printers/${printerId}/papers`)
      .then((r) => {
        setPapers(r.data.papers || []);
        setRecommended(r.data.recommended || []);
        // If the saved value isn't in the list, flip to custom mode
        if (value && !(r.data.papers || []).includes(value)) setCustom(true);
      })
      .catch(() => setPapers([]))
      .finally(() => setLoading(false));
  }, [printerId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <div className="text-[13px] font-semibold text-[#121216]">{label}</div>
          {value && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded-full bg-[#DCFCE7] text-[#2F8F6E]">
              <CheckCircle size={10} weight="fill" />
              SAVED
            </span>
          )}
        </div>
        <div className="text-[11px] text-[#9CA3AF]">{hint}</div>
      </div>
      {!custom ? (
        <select
          value={value || ""}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "__custom__") { setCustom(true); return; }
            onChange(v || null);
          }}
          data-testid={testid}
          disabled={loading}
          className="w-full h-11 px-3 rounded-xl border border-[#E5E7EB] bg-white text-[13px] text-[#121216] focus:outline-none focus:ring-2 focus:ring-[#1E5FEB]/20 disabled:bg-[#F8FAFC]"
        >
          <option value="">{loading ? "Loading driver paper sizes…" : (fallbackHint || "— Not set —")}</option>
          {recommended.length > 0 && (
            <optgroup label="Recommended for thermal / label">
              {recommended.map((p) => (
                <option key={`r-${p}`} value={p}>{p}</option>
              ))}
            </optgroup>
          )}
          {papers.length > 0 && (
            <optgroup label="All driver paper sizes">
              {papers.filter((p) => !recommended.includes(p)).map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </optgroup>
          )}
          <option value="__custom__">Type a custom name…</option>
        </select>
      ) : (
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value || null)}
            placeholder="Exact paper size name as shown in the driver"
            data-testid={`${testid}-custom`}
            className="flex-1 h-11 px-3 rounded-xl border border-[#E5E7EB] bg-white text-[13px] text-[#121216] focus:outline-none focus:ring-2 focus:ring-[#1E5FEB]/20"
          />
          <button
            type="button"
            onClick={() => { setCustom(false); onChange(null); }}
            className="h-11 px-3 rounded-xl border border-[#E5E7EB] text-[12px] text-[#4B5563] hover:bg-[#F8FAFC]"
          >
            Back to list
          </button>
        </div>
      )}
      <p className="mt-1 text-[10px] text-[#9CA3AF] leading-relaxed">
        Pick the exact name your printer's Windows driver uses (e.g. <code className="font-mono">Roll Paper 80 x 297 mm</code>).
        Wrong size = driver pads short receipts with blank paper.
      </p>
    </label>
  );
}

// ---------- Tyro EFTPOS integration card ----------
// Per-store pairing of a physical Tyro terminal. MID + TID are stamped on
// the terminal itself (Menu → Information). The "Pair terminal" button
// lazy-loads the iClient SDK and calls `pairTerminal(mid, tid)` — the
// customer approves on the physical terminal, we persist the returned
// `integration_key` server-side so the pairing survives a browser cache
// wipe.
// =========================================================
// Tyro production SDK API key entry modal.
//
// Cert friendliness:
//   • The input is password-masked (autocomplete=off, type=password).
//   • The actual key value is never re-displayed back to the admin —
//     once saved, the modal closes and only a boolean "configured"
//     flag remains visible.
//   • Status of the key is a one-way fact: "set" or "not set". No
//     reveal button, no clipboard helper.
//   • Confirmation requires the admin's own PIN before save so a
//     stolen session can't quietly drop a rogue key into prod.
function TyroApiKeyModal({ open, storeId, hasKey, onClose, onSaved }) {
  const [pin, setPin] = useState("");
  const [adminPin, setAdminPin] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setPin(""); setAdminPin(""); }
  }, [open]);

  if (!open) return null;

  const save = async (e) => {
    e?.preventDefault?.();
    if (!pin.trim()) return toast.error("Paste the Tyro production API key first");
    if (!/^\d{4,8}$/.test(adminPin)) return toast.error("Enter your admin PIN to authorise");
    setSaving(true);
    try {
      await api.put(`/stores/${storeId}/tyro`, { api_key: pin.trim() }, {
        headers: { "X-Admin-Pin": adminPin },
      });
      toast.success("Tyro production API key saved");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="tyro-api-key-modal-overlay"
    >
      <form
        onSubmit={save}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-lg rounded-[24px] shadow-2xl border border-slate-200/60 overflow-hidden"
        data-testid="tyro-api-key-modal"
      >
        <div className="px-6 pt-6 pb-4 flex items-start gap-3 border-b border-slate-100">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center shadow-md shadow-emerald-500/25 shrink-0">
            <Lock size={18} weight="fill" className="text-white" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-semibold">TYRO PRODUCTION</div>
            <h3 className="font-display font-bold text-[20px] text-slate-900 leading-tight">
              {hasKey ? "Rotate production API key" : "Set production API key"}
            </h3>
            <div className="text-[12px] text-slate-500 mt-1 leading-relaxed">
              Paste the SDK key Tyro emailed you. Stored encrypted, never re-displayed. Live transactions activate immediately after save.
            </div>
          </div>
        </div>

        <div className="px-6 py-5 space-y-4">
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">Tyro production SDK API key</div>
            <input
              type="password"
              autoComplete="off"
              spellCheck={false}
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="Paste the key from Tyro's email"
              data-testid="tyro-api-key-input"
              className="w-full h-12 px-4 font-mono text-[13px] tracking-tight rounded-xl border-2 border-slate-200 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 focus:outline-none"
            />
            <div className="text-[10.5px] text-slate-500 mt-1.5 leading-relaxed">
              {hasKey
                ? "Saving will overwrite the previous key. Use this only if Tyro rotates your key."
                : "Once saved, this field is cleared and the key is never shown back to anyone — including admins."}
            </div>
          </label>

          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">Your admin PIN (to authorise)</div>
            <input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={8}
              autoComplete="off"
              value={adminPin}
              onChange={(e) => setAdminPin(e.target.value.replace(/\D/g, ""))}
              placeholder="••••"
              data-testid="tyro-api-key-admin-pin"
              className="w-full h-12 px-4 text-center font-mono font-bold text-xl tracking-[0.4em] rounded-xl border-2 border-slate-200 focus:border-[#1E5FEB] focus:ring-2 focus:ring-blue-100 focus:outline-none"
            />
          </label>
        </div>

        <div className="px-6 pb-6 pt-2 bg-slate-50/60 border-t border-slate-100 flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 h-11 rounded-xl border border-slate-200 hover:border-slate-400 text-[13px] font-semibold text-slate-700">CANCEL</button>
          <button
            type="submit"
            disabled={saving}
            data-testid="tyro-api-key-save"
            className="flex-1 h-11 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white text-[13px] font-semibold disabled:opacity-60 shadow-md shadow-emerald-500/25"
          >
            {saving ? "SAVING…" : hasKey ? "ROTATE KEY" : "SAVE KEY"}
          </button>
        </div>
      </form>
    </div>
  );
}


function TyroToggle({ label, description, checked, onChange, testid }) {
  // Cert-required ON/OFF toggle (Tyro Settings Page section). Optimistic
  // toggle pattern — flip immediately, parent persists. Description
  // doubles as cert-evidence labelling for assessor screenshots.
  return (
    <label className="flex items-start gap-3 cursor-pointer group" data-testid={testid}>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange?.(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-[#C7D2FE] text-[#1E5FEB] focus:ring-[#1E5FEB]"
      />
      <div className="flex-1">
        <div className="text-[13px] font-semibold text-[#121216] group-hover:text-[#1E40AF]">{label}</div>
        <div className="text-[11px] text-[#6B7280] mt-0.5 leading-snug">{description}</div>
      </div>
    </label>
  );
}

export function TyroIntegrationCard() {
  const { currentStore, refresh: refreshStores } = useStore();
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ env: "test", merchant_id: "", terminal_id: "" });
  // apiKey for production is fetched just-in-time from /sdk-key at the
  // moment of pair / test — never lives in React state, never leaks into
  // any rendered HTML or error log. Test/preprod fall back to the public
  // simulator key automatically. (Removed the old hardcoded `const apiKey
  // = ""` that caused production pair attempts to send `iclient-simulator`
  // and get rejected with 403.)
  const [pairStatus, setPairStatus] = useState("");
  const [apiKeyModalOpen, setApiKeyModalOpen] = useState(false);
  const [pairing, setPairing] = useState(false);
  // Tyro cert `HeadlessPairing.Failed.2`: when MID/TID is wrong (simulator
  // returns 400 Bad Request) the pairing UI must clearly surface the
  // failure — not just a transient toast. This banner stays visible until
  // the next pair attempt so the reviewer can see the error message on
  // the recording.
  const [pairError, setPairError] = useState("");
  const [saving, setSaving] = useState(false);
  // Embedded iClient log viewer toggle — opens an iframe ON THIS PAGE
  // so it shares the same browser storage partition as our SDK iframe.
  // (Opening logs.html in a separate tab is partitioned away from our
  // SDK writes — see comment block where the iframe is rendered.)
  const [showEmbeddedLog, setShowEmbeddedLog] = useState(false);

  const load = async () => {
    if (!currentStore?.id) return;
    try {
      const { data } = await api.get(`/stores/${currentStore.id}/tyro`);
      setCfg(data);
      setForm({
        env: data.env || "test",
        merchant_id: data.merchant_id || "",
        terminal_id: data.terminal_id || "",
      });
    } catch { /* store has no config yet; stays null */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [currentStore?.id]);

  const saveConfig = async (patch = {}) => {
    if (!currentStore?.id) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/stores/${currentStore.id}/tyro`, { ...form, ...patch });
      setCfg(data);
      // CRITICAL: refresh the StoreContext so other tabs (POS, SaleDetail,
      // TyroPaymentDialog) see the new flag values IMMEDIATELY. Without
      // this, the cashier can toggle "Print integrated receipts" OFF in
      // settings but the dialog keeps reading the stale store object,
      // sending integratedReceipt: true to the SDK and auto-printing on
      // success — directly failing cert non-integratedReceipt.Purchase.1.
      await refreshStores();
      return data;
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const unpair = async () => {
    if (!window.confirm("Unpair this terminal? You'll need to re-pair on the physical device before taking another Tyro payment.")) return;
    try {
      await api.delete(`/stores/${currentStore.id}/tyro`);
      toast.success("Terminal unpaired");
      await refreshStores();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Diagnostic ping — proves the terminal is still online and the paired
  // integration_key is valid. Doesn't take a payment. Matches Tyro's
  // documented `terminalInfo` method.
  const [testing, setTesting] = useState(false);
  const testConnection = async () => {
    setTesting(true);
    setPairStatus("Pinging terminal…");
    try {
      // Same pattern as the pair flow above — fetch the right key for env.
      let sdkApiKey = "";
      try {
        const r = await api.get(`/stores/${currentStore?.id}/tyro/sdk-key`);
        sdkApiKey = r?.data?.api_key || "";
      } catch (err) {
        toast.error(formatApiError(err) || "Production API key not configured");
        setTesting(false);
        return;
      }
      const tyro = await initTyro({
        env: cfg?.env || form.env,
        apiKey: sdkApiKey,
        posProduct: {
          vendor: "iRepair Experts", name: "iRepair POS", version: "1.0.0",
          siteReference: currentStore?.name || "default",
        },
      });
      // The integration key returned by pairTerminal is stored on the
      // Store record and exposed via /api/stores. The SDK requires it on
      // every transaction (including terminalInfo) because each browser
      // instance starts with no memory of the previous pairing.
      const integrationKey = currentStore?.tyro_integration_key || "";
      if (!integrationKey) {
        toast.error("Integration key missing — re-pair the terminal first");
        return;
      }
      await tyro.terminalInfo({
        mid: cfg?.merchant_id,
        tid: cfg?.terminal_id,
        integrationKey,
      });
      toast.success("Terminal online ✓");
    } catch (e) {
      toast.error(e?.message || "Terminal unreachable");
    } finally {
      setTesting(false);
      setPairStatus("");
    }
  };

  const doPair = async () => {
    if (!form.merchant_id.trim() || !form.terminal_id.trim()) {
      return toast.error("Enter both Merchant ID (MID) and Terminal ID (TID) first");
    }
    // Persist MID/TID/env first so the config survives a page refresh even
    // if pairing fails.
    await saveConfig({ enabled: true });
    setPairing(true);
    setPairError("");   // clear any prior error banner
    setPairStatus("Loading Tyro SDK…");
    try {
      // Fetch the SDK API key just-in-time. In test/preprod the backend
      // returns the public simulator key; in prod it returns the real key
      // the merchant saved via the API key modal. Doing this here (not on
      // mount) keeps the key out of React state and out of any error log
      // / replay snapshot. Replaces the old hardcoded apiKey="" fallback
      // which silently sent `iclient-simulator` to production and got 403.
      let sdkApiKey = "";
      try {
        const r = await api.get(`/stores/${currentStore?.id}/tyro/sdk-key`);
        sdkApiKey = r?.data?.api_key || "";
      } catch (err) {
        setPairError(formatApiError(err) || "Production API key not configured — set it via the green status card below.");
        setPairing(false);
        return;
      }
      const tyro = await initTyro({
        env: form.env,
        apiKey: sdkApiKey,
        posProduct: {
          vendor: "iRepair Experts",
          name: "iRepair POS",
          version: "1.0.0",
          siteReference: currentStore?.name || "default",
        },
      });
      setPairStatus(
        form.env === "test"
          ? "Contacting simulator… (no physical approval needed)"
          : "Press YES on the terminal to approve pairing…"
      );
      const { integrationKey } = await tyro.pair({
        mid: form.merchant_id,
        tid: form.terminal_id,
        onStatus: (m) => setPairStatus(m || "Pairing…"),
      });
      await saveConfig({ integration_key: integrationKey, enabled: true });
      // Refresh the global stores list so currentStore.tyro_integration_key
      // is hydrated immediately — the cert harness and TyroPaymentDialog
      // both read from there for subsequent transactions.
      await refreshStores();
      toast.success("Terminal paired ✓");
      setPairStatus("");
      load();
    } catch (e) {
      setPairStatus("");
      // Persistent error banner. Tyro cert HeadlessPairing.Failed.2 needs
      // the message visible until the cashier dismisses it / retries.
      const msg = e?.message || "Pairing failed";
      setPairError(msg);
      toast.error(msg);
    } finally {
      setPairing(false);
    }
  };

  const envMeta = TYRO_ENVS.find((e) => e.value === (cfg?.env || form.env));
  const statusChipTone = cfg?.paired
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : cfg?.enabled
      ? "bg-amber-50 text-amber-700 border-amber-200"
      : "bg-slate-50 text-slate-600 border-slate-200";
  const statusChipLabel = cfg?.paired ? "PAIRED" : cfg?.enabled ? "NOT PAIRED" : "OFF";

  return (
    <section
      className="rounded-2xl border bg-white p-6"
      style={{ borderColor: "#E5E7EB" }}
      data-testid="tyro-integration-card"
    >
      <div className="flex items-start gap-4">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 bg-white border border-[#E5E7EB]"
        >
          <img
            src="/tyro-logo.gif"
            alt="Tyro"
            className="w-10 h-10 object-contain"
            data-testid="tyro-logo"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-semibold">Tyro EFTPOS · counter-top terminal</div>
            <span
              className={`text-[9px] font-black tracking-[0.15em] px-1.5 py-0.5 rounded border ${statusChipTone}`}
              data-testid="tyro-status-chip"
            >{statusChipLabel}</span>
            {/* Tyro Phase 1 certification was awarded on 2026-06-22.
                Shown as a permanent badge so reviewers and admins can
                confirm at a glance. Certified feature set: integrated
                purchases, refunds, receipts, surcharging, split
                payments, reports, POS info, settings page. */}
            <span
              className="inline-flex items-center gap-1 text-[9px] font-black tracking-[0.15em] px-1.5 py-0.5 rounded bg-emerald-600 text-white"
              title="Tyro Phase 1 certification awarded 22 June 2026"
              data-testid="tyro-certified-badge"
            >
              <CheckCircle size={10} weight="fill" />
              TYRO CERTIFIED
            </span>
            {/* Active store pill — every Tyro config is per-store, so the
                MID/TID/API key shown below are for THIS store only. When
                an admin manages multiple shops they switch stores via the
                sidebar and this card auto-reloads with the new store's
                terminal config. */}
            {currentStore && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.12em] px-2 py-1 rounded bg-blue-50 text-[#1E5FEB] border border-blue-200"
                data-testid="tyro-store-pill"
              >
                <Storefront size={11} weight="bold" />
                For store · {currentStore.name}
              </span>
            )}
          </div>
          <div className="mt-1 font-display text-[22px] font-semibold tracking-tight text-[#121216]">
            Tyro Pair@POS
          </div>
          <p className="text-[12px] text-[#4B5563] mt-2 leading-relaxed max-w-2xl">
            Pair a physical Tyro terminal to push card payments directly from checkout. MID + TID are printed on the terminal — press <strong>MENU → Information</strong>. Requires the Tyro Terminal Adapter installed on the counter PC. Each store has its own terminal config — switch stores in the sidebar to manage another location. <span className="text-[#1E40AF] font-semibold">Sandbox is free to use — pair with MID 795 / TID 4 without any Tyro key.</span>
          </p>
          {cfg?.paired && cfg?.paired_at && (
            <p className="text-[11px] text-emerald-700 mt-2 font-mono">
              ✓ Last paired {new Date(cfg.paired_at).toLocaleString()}
            </p>
          )}
        </div>
      </div>

      {/* Production SaaS model: every store runs against Tyro production.
          The SDK API key is managed centrally by the platform operator
          via the TYRO_PROD_API_KEY env var on the deployment — merchants
          never see it, can never leak it, and only need to enter their
          own MID + TID below to pair their physical terminal. The old
          environment switcher + key-entry modal have been removed to
          match the SaaS distribution model. */}

      {/* Config grid */}
      <div className="mt-5 p-5 rounded-2xl border border-[#E5E7EB] bg-[#FAFAFA] space-y-4">

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
        <div>
          <div className="text-[11px] tracking-[0.14em] uppercase text-[#6B7280] font-semibold mb-1.5">Merchant ID (MID)</div>
          <input
            value={form.merchant_id}
            onChange={(e) => setForm({ ...form, merchant_id: e.target.value })}
            placeholder={form.env === "test" ? "795 for simulator" : "e.g. 12345678"}
            data-testid="tyro-mid"
            className="w-full h-10 px-3 rounded-lg border border-[#E5E7EB] bg-white text-[13px] font-mono text-[#121216] focus:border-[#1E5FEB] focus:outline-none"
          />
        </div>
        <div>
          <div className="text-[11px] tracking-[0.14em] uppercase text-[#6B7280] font-semibold mb-1.5">Terminal ID (TID)</div>
          <input
            value={form.terminal_id}
            onChange={(e) => setForm({ ...form, terminal_id: e.target.value })}
            placeholder={form.env === "test" ? "4 for simulator" : "e.g. 87654321"}
            data-testid="tyro-tid"
            className="w-full h-10 px-3 rounded-lg border border-[#E5E7EB] bg-white text-[13px] font-mono text-[#121216] focus:border-[#1E5FEB] focus:outline-none"
          />
        </div>
      </div>
      </div>

      {/* Production SDK API key.
          --------------------------------------------------------------
          Per Tyro cert `API.Key.Configuration.1`, the production API key
          must NEVER be visible or editable by merchants through any
          front-end UI. So we deliberately do NOT render an input here.

          The key is configured server-side by Emergent ops (or via a
          one-time admin curl) and stored encrypted on the store doc.
          The Tyro payment dialog fetches it just-in-time from
          `/api/stores/{id}/tyro/sdk-key` (admin-auth-gated) when the
          cashier presses "Pay with Tyro" — the value never lives in
          this JS bundle.

          We DO render a status indicator below so admins know whether
          live mode is actually wired up. */}
      {/* SaaS model — single central Tyro key managed by the platform
          operator via TYRO_PROD_API_KEY env var. No per-merchant key UI,
          no environment switch. Merchants enter MID/TID above and pair
          their terminal — that's it. */}

      {/* Live pairing status */}
      {pairStatus && (
        <div
          className="mt-4 rounded-xl border px-4 py-3 text-[12.5px] font-medium flex items-center gap-2"
          style={{ background: "#FFFBEB", borderColor: "#FDE68A", color: "#92400E" }}
          data-testid="tyro-pair-status"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
          {pairStatus}
        </div>
      )}

      {/* Persistent pairing error banner (Tyro cert HeadlessPairing.Failed.2).
          Stays visible until the cashier fixes MID/TID and re-pairs. */}
      {pairError && (
        <div
          className="mt-4 rounded-xl border px-4 py-3 text-[12.5px] font-medium flex items-start gap-2"
          style={{ background: "#FEF2F2", borderColor: "#FCA5A5", color: "#991B1B" }}
          data-testid="tyro-pair-error"
        >
          <div className="w-4 h-4 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">!</div>
          <div className="flex-1">
            <div className="font-bold uppercase tracking-wider text-[10px] mb-0.5">Pairing failed</div>
            <div className="font-mono text-[12px] leading-relaxed">{pairError}</div>
            <div className="text-[11px] mt-1.5 text-red-700/80">
              Check the Merchant ID and Terminal ID match what's printed on the terminal (MENU → Information), then click Pair terminal again.
            </div>
          </div>
          <button
            type="button"
            onClick={() => setPairError("")}
            className="text-red-600 hover:text-red-800 text-[11px] font-semibold uppercase tracking-wider"
            data-testid="tyro-pair-error-dismiss"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Actions */}
      <div className="mt-5 flex items-center gap-2 flex-wrap">
        {!cfg?.paired ? (
          <button
            type="button"
            onClick={doPair}
            disabled={pairing || saving || !form.merchant_id.trim() || !form.terminal_id.trim()}
            data-testid="tyro-pair-btn"
            className="h-10 px-5 rounded-lg bg-[#1E5FEB] hover:bg-[#1849C8] disabled:bg-slate-300 text-white text-[13px] font-semibold flex items-center gap-1.5 transition-colors"
            style={{ boxShadow: "0 2px 8px rgba(30,95,235,0.35)" }}
          >
            <CreditCard size={14} weight="bold" /> {pairing ? "Pairing…" : "Pair terminal"}
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={testConnection}
              disabled={testing || pairing}
              data-testid="tyro-test-btn"
              className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[13px] font-semibold flex items-center gap-1.5 disabled:bg-slate-300"
            >
              <CheckCircle size={13} weight="bold" /> {testing ? "Pinging…" : "Test connection"}
            </button>
            <button
              type="button"
              onClick={unpair}
              data-testid="tyro-unpair-btn"
              className="h-10 px-4 rounded-lg border border-rose-200 hover:bg-rose-50 text-rose-700 text-[13px] font-semibold flex items-center gap-1.5"
            >
              <Trash size={13} weight="bold" /> Unpair
            </button>
          </>
        )}
        <button
          type="button"
          onClick={() => saveConfig({})}
          disabled={saving || pairing}
          className="h-10 px-4 rounded-lg border border-[#E5E7EB] hover:border-[#121216] bg-white text-[#4B5563] hover:text-[#121216] text-[13px] font-semibold transition-colors"
          data-testid="tyro-save-btn"
        >
          {saving ? "Saving…" : "Save without pairing"}
        </button>
      </div>

      {/* ——— Tyro cert "Tyro Settings Page" toggles ———
          Required by Tyro cert (Compulsory section) — every iClient
          integration must expose ON/OFF toggles for these from a single
          settings panel + a button to view the iClient logs page. */}
      {cfg && (
        <div className="mt-6 rounded-xl border border-[#E5E7EB] bg-white p-5" data-testid="tyro-settings-panel">
          <div className="text-[11px] uppercase tracking-[0.14em] text-[#6B7280] font-semibold mb-3">
            Terminal behaviour
          </div>
          <div className="space-y-3">
            <TyroToggle
              label="Save EFTPOS Merchant + Customer copies on every sale"
              description="When ON, the Tyro iClient SDK is asked to return the full Merchant Copy and Customer Copy receipt text on every approved purchase. The copies are saved against the sale and surface as download buttons in the 'Tyro EFTPOS Receipts' panel on the invoice page — so a customer can walk back in months later and you can re-print or email their EFTPOS slip on demand. Required ON for cert. Disable only if you don't want to store EFTPOS receipt copies."
              checked={cfg.save_eftpos_copies !== false}
              onChange={(v) => saveConfig({ save_eftpos_copies: v })}
              testid="tyro-toggle-save-copies"
            />
            <TyroToggle
              label="Print integrated receipts on thermal"
              description="When ON, the saved Merchant + Customer EFTPOS copies are ALSO appended to the thermal sale receipt that prints automatically after approval. When OFF, the Tyro summary (Card / Auth / RRN / Ref) still prints on the thermal — but the full slip copies aren't appended. Has no effect on the invoice page's download buttons (those are controlled by 'Save EFTPOS copies' above)."
              checked={!!cfg.print_integrated_receipts}
              onChange={(v) => saveConfig({ print_integrated_receipts: v })}
              testid="tyro-toggle-integrated-receipts"
            />
            <TyroToggle
              label="Print signature merchant copy"
              description="When the cardholder signs (chip+PIN bypass), print a separate merchant copy with a signature line. Required for cert."
              checked={!!cfg.print_signature_merchant_copy}
              onChange={(v) => saveConfig({ print_signature_merchant_copy: v })}
              testid="tyro-toggle-sig-merchant"
            />
            <TyroToggle
              label="Apply Tyro surcharge"
              description="Send enableSurcharge: true on every purchase so Tyro applies the surcharge configured against your MID. Disable only if your POS handles surcharging itself."
              checked={cfg.surcharge_enabled !== false}
              onChange={(v) => saveConfig({ surcharge_enabled: v })}
              testid="tyro-toggle-surcharge"
            />
          </div>

          {/* iClient logs — cert spec point 2c.
              IMPORTANT: opening logs.html in a NEW TAB triggers Chrome's
              third-party storage partitioning — the tab reads from the
              unpartitioned tyro origin while our SDK iframe writes to the
              partitioned (parent=our app, embedded=tyro) scope, so the
              tab will appear empty even after an approved transaction.
              Embedding the log viewer in THIS page puts it in the same
              partition as the SDK iframe, so it sees every event and the
              "Send to Tyro" button posts the right payload. */}
          <div className="mt-4 pt-3 border-t border-[#F3F4F6] flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setShowEmbeddedLog((v) => !v)}
              className="h-9 px-3 rounded-lg bg-[#121216] hover:bg-[#1E5FEB] text-white text-[12px] font-bold inline-flex items-center gap-1.5 transition-colors"
              data-testid="tyro-embedded-log-toggle"
            >
              <ListBullets size={12} weight="bold" /> {showEmbeddedLog ? "Hide" : "Show"} embedded iClient log
            </button>
            <a
              href={cfg.env === "prod"
                ? "https://iclient.tyro.com/logs.html#expert"
                : "https://iclientsimulator.test.tyro.com/logs.html#expert"}
              target="_blank"
              rel="noopener noreferrer"
              className="h-9 px-3 rounded-lg border border-[#E5E7EB] hover:border-[#121216] text-[#4B5563] hover:text-[#121216] text-[12px] font-semibold inline-flex items-center gap-1.5"
              data-testid="tyro-view-logs-btn"
            >
              Open in new tab
            </a>
            <span className="text-[11px] text-[#6B7280]">
              ⚠ New tab uses a different storage partition — embedded viewer is the cert-correct evidence source.
            </span>
          </div>

          {showEmbeddedLog && (
            <div className="mt-4" data-testid="tyro-embedded-log-iframe-wrap">
              <iframe
                title="Tyro iClient log viewer"
                src={cfg.env === "prod"
                  ? "https://iclient.tyro.com/logs.html#expert"
                  : "https://iclientsimulator.test.tyro.com/logs.html#expert"}
                className="w-full rounded-xl border border-[#E5E7EB] bg-white"
                style={{ height: "640px" }}
                data-testid="tyro-embedded-log-iframe"
              />
              <p className="mt-2 text-[11px] text-[#6B7280] leading-relaxed">
                This embedded viewer shares the same browser storage partition as the SDK iframe — so every iClient request your POS makes (purchases, refunds, pairs, terminalInfo) appears here. Click <strong>Display data</strong> in the iframe to expand the log, then <strong>Send to Tyro</strong> when you have the evidence row(s) you need.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Tyro cert harness (Phase 1 / Phase 2 / Purchase flow / Refund
          flow scenario buttons) was removed at the merchant's request
          — that workflow is being driven directly from the simulator
          + embedded log viewer above. The Reconciliation card below
          covers Recon.Report.1 / .2 / .3 / .4 / .5 cert criteria.
          Legacy import is kept in case the user wants to bring it back
          via /settings/legacy. */}
      <TyroReconciliationCard />
    </section>
  );
}


// ---------- IntegrationsTab ----------
export function IntegrationsTab() {
  const [data, setData] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [migrateOpen, setMigrateOpen] = useState(false);
  const [grossOpen, setGrossOpen] = useState(false);

  const load = () => api.get("/integrations/booking").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch { toast.error("Clipboard unavailable"); }
  };

  const rotate = async () => {
    if (!window.confirm("Rotate the Booking API key? The old key will stop working immediately — you'll need to update your booking website.")) return;
    setRotating(true);
    try {
      await api.post("/integrations/booking/rotate");
      toast.success("Key rotated");
      setRevealed(true);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRotating(false);
    }
  };

  if (!data) return <div className="p-6 text-sm text-[#9CA3AF]">Loading…</div>;

  const maskedKey = data.api_key ? `${data.api_key.slice(0, 4)}${"•".repeat(Math.max(0, data.api_key.length - 8))}${data.api_key.slice(-4)}` : "";
  const sampleJson = JSON.stringify(data.sample_payload, null, 2);
  const curlExample = `curl -X POST "${data.endpoint}" \\
  -H "Content-Type: application/json" \\
  -H "X-Booking-Key: ${data.api_key}" \\
  -d '${JSON.stringify(data.sample_payload)}'`;

  return (
    <div className="p-6 max-w-4xl space-y-6">
      <TyroIntegrationCard />
      <section className="rounded-2xl border border-[#E5E7EB] bg-white p-6" data-testid="booking-integration-card">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-full bg-[#DBEAFE] flex items-center justify-center shrink-0">
            <CalendarCheck size={22} weight="duotone" className="text-[#1E5FEB]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium">
              Booking website &rarr; POS
            </div>
            <div className="mt-1 font-display text-[20px] font-semibold tracking-tight text-[#121216]">
              Inbound bookings webhook
            </div>
            <p className="text-[12px] text-[#4B5563] mt-2 leading-relaxed">
              Give these two values to the agent running your booking website. It will <code className="font-mono text-[11px] bg-[#F8FAFC] px-1 rounded">POST</code> every new booking to the endpoint below. Bookings land in the <a href="/bookings" className="text-[#1E5FEB] hover:underline">Bookings</a> page where your counter staff can confirm, cancel, or convert them into repair tickets.
            </p>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          <KVRow
            label="Endpoint URL"
            value={data.endpoint}
            icon={<LinkIcon size={14} weight="bold" />}
            testid="booking-endpoint"
            onCopy={() => copy(data.endpoint, "Endpoint URL")}
          />
          <KVRow
            label="Header name"
            value={data.header_name}
            testid="booking-header"
            onCopy={() => copy(data.header_name, "Header name")}
            monospace
          />
          <KVRow
            label="API key"
            value={revealed ? data.api_key : maskedKey}
            testid="booking-api-key"
            onCopy={() => copy(data.api_key, "API key")}
            monospace
            trailing={
              <>
                <button
                  onClick={() => setRevealed((v) => !v)}
                  className="text-[11px] px-2.5 py-1 rounded-full border border-[#E5E7EB] hover:border-[#121216] text-[#4B5563] hover:text-[#121216] font-medium transition-colors"
                  data-testid="booking-reveal-key"
                >
                  {revealed ? "Hide" : "Show"}
                </button>
                <button
                  onClick={rotate}
                  disabled={rotating}
                  data-testid="booking-rotate-key"
                  className="text-[11px] px-2.5 py-1 rounded-full border border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white font-medium flex items-center gap-1 transition-colors"
                >
                  <ArrowsClockwise size={11} weight="bold" /> Rotate
                </button>
              </>
            }
          />
        </div>
      </section>

      <section className="rounded-2xl border border-[#E5E7EB] bg-white p-6">
        <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium mb-3">
          Sample payload
        </div>
        <pre className="bg-[#0F172A] text-[#E0E7FF] font-mono text-[11px] leading-relaxed p-4 rounded-xl overflow-auto whitespace-pre" data-testid="booking-sample-json">{sampleJson}</pre>
        <button
          onClick={() => copy(sampleJson, "Sample payload")}
          className="mt-3 h-9 px-3 rounded-full border border-[#E5E7EB] hover:border-[#121216] text-[12px] text-[#4B5563] hover:text-[#121216] font-medium flex items-center gap-2 transition-colors"
        >
          <Copy size={12} weight="bold" /> Copy JSON
        </button>
      </section>

      <section className="rounded-2xl border border-[#E5E7EB] bg-white p-6">
        <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium mb-3">
          cURL example
        </div>
        <pre className="bg-[#0F172A] text-[#E0E7FF] font-mono text-[11px] leading-relaxed p-4 rounded-xl overflow-auto whitespace-pre" data-testid="booking-curl">{curlExample}</pre>
        <button
          onClick={() => copy(curlExample, "cURL command")}
          className="mt-3 h-9 px-3 rounded-full border border-[#E5E7EB] hover:border-[#121216] text-[12px] text-[#4B5563] hover:text-[#121216] font-medium flex items-center gap-2 transition-colors"
        >
          <Copy size={12} weight="bold" /> Copy cURL
        </button>
      </section>

      <section className="rounded-2xl border border-[#E5E7EB] bg-[#F8FAFC] p-6">
        <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium mb-2">Field reference</div>
        <ul className="text-[12px] text-[#4B5563] leading-relaxed space-y-1.5">
          <li><code className="font-mono text-[11px] text-[#121216]">customer_name</code> - required</li>
          <li><code className="font-mono text-[11px] text-[#121216]">customer_phone</code> / <code className="font-mono text-[11px] text-[#121216]">customer_email</code> - at least one recommended (used for customer dedupe)</li>
          <li><code className="font-mono text-[11px] text-[#121216]">device_make</code>, <code className="font-mono text-[11px] text-[#121216]">device_model</code>, <code className="font-mono text-[11px] text-[#121216]">problem</code></li>
          <li><code className="font-mono text-[11px] text-[#121216]">model_number</code> (Apple A-series, e.g. "A2848") and <code className="font-mono text-[11px] text-[#121216]">year</code> ("2023") - optional but recommended so techs can identify the exact device when ordering parts</li>
          <li><code className="font-mono text-[11px] text-[#121216]">preferred_at</code> - ISO 8601 timestamp (e.g. 2026-02-15T10:30:00+10:00)</li>
          <li><code className="font-mono text-[11px] text-[#121216]">store_id</code> or <code className="font-mono text-[11px] text-[#121216]">store_name</code> - booking is tagged to this store (fuzzy match supported, e.g. "Coorparoo")</li>
          <li><code className="font-mono text-[11px] text-[#121216]">notes</code> - any extra instructions from the customer</li>
        </ul>
      </section>

      {/* Migrate from another POS */}
      <section className="rounded-2xl border border-[#E5E7EB] bg-white p-6" data-testid="migrate-card">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-full bg-[#DCFCE7] flex items-center justify-center shrink-0">
            <Database size={22} weight="duotone" className="text-[#2F8F6E]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium">
              One-time migration
            </div>
            <div className="mt-1 font-display text-[20px] font-semibold tracking-tight text-[#121216]">
              Import from another POS
            </div>
            <p className="text-[12px] text-[#4B5563] mt-2 leading-relaxed">
              Upload a <code className="font-mono text-[11px] bg-[#F8FAFC] px-1 rounded">.xlsx</code> export from <strong>RepairDesk</strong>, <strong>RepairShopr</strong>, or any POS with <strong>Customers</strong> / <strong>Tickets</strong> / <strong>Invoices</strong> sheets. We auto-detect sheets, auto-map columns, and link tickets & invoices to customers by phone/email.
            </p>
          </div>
          <button
            onClick={() => setMigrateOpen(true)}
            data-testid="open-migrate-modal"
            className="h-10 px-4 rounded-full bg-[#121216] hover:bg-[#1E5FEB] text-white text-[12px] font-semibold flex items-center gap-2 transition-colors"
          >
            <Database size={13} weight="bold" /> Start migration
          </button>
        </div>
      </section>

      <SmsDeliveryLogCard />

      {/* Import historical gross (RepairDesk) */}
      <section className="rounded-2xl border border-[#E5E7EB] bg-white p-6" data-testid="import-gross-card">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-full bg-[#FEF3C7] flex items-center justify-center shrink-0">
            <ChartLineUp size={22} weight="duotone" className="text-[#B45309]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium">
              Reports history
            </div>
            <div className="mt-1 font-display text-[20px] font-semibold tracking-tight text-[#121216]">
              Import RepairDesk historical gross
            </div>
            <p className="text-[12px] text-[#4B5563] mt-2 leading-relaxed">
              Upload the <strong>Multi-Store Summary</strong> <code className="font-mono text-[11px] bg-[#F8FAFC] px-1 rounded">.xlsx</code> from RepairDesk to add a pre-migration revenue baseline per store. These totals get folded into the <a href="/reports" className="text-[#1E5FEB] hover:underline">Reports</a> tab so "All time" / "Last year" reflect your true business history — not just sales made in this app.
            </p>
          </div>
          <button
            onClick={() => setGrossOpen(true)}
            data-testid="open-gross-modal"
            className="h-10 px-4 rounded-full bg-[#121216] hover:bg-[#B45309] text-white text-[12px] font-semibold flex items-center gap-2 transition-colors"
          >
            <ChartLineUp size={13} weight="bold" /> Upload summary
          </button>
        </div>
      </section>

      <ImportGrossModal open={grossOpen} onClose={() => setGrossOpen(false)} onImported={() => {}} />
      <MigrateFromPOSModal open={migrateOpen} onClose={() => setMigrateOpen(false)} />
    </div>
  );
}

// Dummy close to keep diff minimal — original IntegrationsTab function ended right above.

// ---------- SmsDeliveryLogCard ----------
export function SmsDeliveryLogCard() {
  const [rows, setRows] = useState(null);
  const [from, setFrom] = useState("");
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const r = await api.get("/integrations/sms/log?limit=30");
      setRows(r.data.messages || []);
      setFrom(r.data.from || "");
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
      setRows([]);
    } finally {
      setRefreshing(false);
    }
  };
  useEffect(() => {
    load();
    // Auto-refresh every 20s while the tab is open so status transitions
    // (queued → sent → delivered) appear without the user hitting reload.
    const t = setInterval(() => load(true), 20000);
    return () => clearInterval(t);
  }, []);

  const stats = (rows || []).reduce((acc, m) => {
    const s = (m.status || "").toLowerCase();
    if (s === "delivered") acc.ok += 1;
    else if (s === "sent" || s === "queued" || s === "sending" || s === "accepted") acc.pending += 1;
    else if (s === "undelivered" || s === "failed") acc.bad += 1;
    else acc.other += 1;
    return acc;
  }, { ok: 0, pending: 0, bad: 0, other: 0 });

  return (
    <section className="rounded-2xl border border-[#E5E7EB] bg-white p-6" data-testid="sms-delivery-log-card">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-full bg-[#DCFCE7] flex items-center justify-center shrink-0">
          <ChatCircleText size={22} weight="duotone" className="text-[#16A34A]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium">SMS delivery log</div>
          <div className="mt-1 font-display text-[20px] font-semibold tracking-tight text-[#121216]">
            Last 30 outbound messages
          </div>
          <p className="text-[12px] text-[#4B5563] mt-2 leading-relaxed">
            Live from Twilio. Catches silent carrier drops (error 30007) and to=from mistakes (21266) so you don't have to open the Twilio console.
            {from && <> Sender: <code className="font-mono text-[11px] bg-[#F8FAFC] px-1 rounded">{from}</code></>}
          </p>
        </div>
        <button
          onClick={() => load()}
          disabled={refreshing}
          data-testid="sms-log-refresh"
          className="h-9 px-3 rounded-full border border-[#E5E7EB] hover:border-[#121216] hover:bg-[#F8FAFC] text-[11px] font-semibold flex items-center gap-1.5 shrink-0 disabled:opacity-50"
        >
          <ArrowsClockwise size={12} weight="bold" className={refreshing ? "animate-spin" : ""} />
          {refreshing ? "LOADING" : "REFRESH"}
        </button>
      </div>

      {error ? (
        <div className="mt-5 rounded-xl border border-[#FCA5A5] bg-[#FEF2F2] px-4 py-3 text-[12px] text-[#B91C1C]" data-testid="sms-log-error">
          {error}
        </div>
      ) : (
        <>
          <div className="mt-5 grid grid-cols-4 gap-2">
            <StatPill label="Delivered" value={stats.ok} tone="ok" />
            <StatPill label="In flight" value={stats.pending} tone="pending" />
            <StatPill label="Undelivered" value={stats.bad} tone="bad" />
            <StatPill label="Other" value={stats.other} tone="other" />
          </div>

          <div className="mt-5 border border-[#E5E7EB] rounded-xl overflow-hidden">
            {!rows ? (
              <div className="p-6 text-[12px] text-[#9CA3AF]">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="p-6 text-[12px] text-[#9CA3AF]">No outbound SMS in the last batch.</div>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-[12px]" data-testid="sms-log-table">
                  <thead className="bg-[#F8FAFC] sticky top-0">
                    <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-[#9CA3AF]">
                      <th className="px-3 py-2 font-medium">When</th>
                      <th className="px-3 py-2 font-medium">To</th>
                      <th className="px-3 py-2 font-medium">Status</th>
                      <th className="px-3 py-2 font-medium">Preview</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((m) => (
                      <SmsLogRow key={m.sid} m={m} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}

function StatPill({ label, value, tone }) {
  const tones = {
    ok: "bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]",
    pending: "bg-[#DBEAFE] text-[#1E40AF] border-[#BFDBFE]",
    bad: "bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]",
    other: "bg-[#F3F4F6] text-[#4B5563] border-[#E5E7EB]",
  }[tone];
  return (
    <div className={`rounded-xl border px-3 py-2 ${tones}`} data-testid={`sms-stat-${tone}`}>
      <div className="text-[10px] uppercase tracking-[0.14em] opacity-80">{label}</div>
      <div className="font-display text-[22px] font-bold leading-tight">{value}</div>
    </div>
  );
}

function SmsLogRow({ m }) {
  const status = (m.status || "").toLowerCase();
  const badge = {
    delivered: "bg-[#DCFCE7] text-[#166534]",
    sent: "bg-[#DBEAFE] text-[#1E40AF]",
    queued: "bg-[#DBEAFE] text-[#1E40AF]",
    sending: "bg-[#DBEAFE] text-[#1E40AF]",
    accepted: "bg-[#DBEAFE] text-[#1E40AF]",
    undelivered: "bg-[#FEE2E2] text-[#B91C1C]",
    failed: "bg-[#FEE2E2] text-[#B91C1C]",
  }[status] || "bg-[#F3F4F6] text-[#4B5563]";

  const when = m.date_created ? new Date(m.date_created) : null;
  const whenStr = when ? when.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
  const errLine = m.error_code ? `err ${m.error_code}${m.error_message ? ` · ${m.error_message}` : ""}` : null;

  return (
    <tr className="border-t border-[#E5E7EB] hover:bg-[#F8FAFC]" data-testid={`sms-log-row-${m.sid}`}>
      <td className="px-3 py-2 text-[#4B5563] whitespace-nowrap">{whenStr}</td>
      <td className="px-3 py-2 font-mono text-[#121216] whitespace-nowrap">{m.to}</td>
      <td className="px-3 py-2 whitespace-nowrap">
        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${badge}`}>
          {status || "unknown"}
        </span>
        {errLine && <div className="text-[10px] text-[#B91C1C] mt-0.5">{errLine}</div>}
      </td>
      <td className="px-3 py-2 text-[#4B5563] max-w-md truncate" title={m.body}>{m.body}</td>
    </tr>
  );
}

function KVRow({ label, value, monospace, onCopy, trailing, testid, icon }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-28 text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium shrink-0">{label}</div>
      <div className="flex-1 min-w-0 flex items-center gap-2 h-10 px-3 rounded-xl border border-[#E5E7EB] bg-[#F8FAFC]">
        {icon && <span className="text-[#9CA3AF] shrink-0">{icon}</span>}
        <span className={`flex-1 min-w-0 truncate text-[12px] ${monospace ? "font-mono" : ""} text-[#121216]`} data-testid={testid}>
          {value}
        </span>
      </div>
      <button
        onClick={onCopy}
        className="h-10 w-10 rounded-xl border border-[#E5E7EB] hover:border-[#121216] hover:bg-[#F8FAFC] text-[#4B5563] hover:text-[#121216] flex items-center justify-center transition-colors shrink-0"
        title={`Copy ${label}`}
      >
        <Copy size={13} weight="bold" />
      </button>
      {trailing && <div className="flex items-center gap-1.5 shrink-0">{trailing}</div>}
    </div>
  );
}

